import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { toast } from 'sonner';
import {
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Eye,
  FileText,
  FolderKanban,
  Info,
  Loader2,
  MessageSquare,
  Mic,
  Search,
  ShieldCheck,
  Sparkles,
  Sword,
  Target,
} from 'lucide-react';
import JollyDictationButton from '@/components/JollyDictationButton';
import CollaborationStrip from '@/components/CollaborationStrip';
import LinkPreviewPanel from '@/components/LinkPreviewPanel';
import FeatureHistoryMenu from '@/components/FeatureHistoryMenu';
import { useAuth } from '@/context/AuthContext';
import { API } from '@/lib/api';
import { readEventStream } from '@/lib/sse';
import { useIsMobile } from '@/lib/utils';

const AINA_PENDING_EDIT_STORAGE_KEY = 'aina_pending_edit';

const LENS_OPTIONS = [
  {
    value: 'opposing_counsel_attack',
    label: 'Opposing Counsel Attack',
    description: 'Stress-test what the other side will attack first.',
    accent: '#f97316',
    tone: 'rgba(249,115,22,0.14)',
  },
  {
    value: 'bench_questions',
    label: 'Bench Questions',
    description: 'Surface the questions that could expose confusion or mismatch.',
    accent: '#38bdf8',
    tone: 'rgba(56,189,248,0.14)',
  },
  {
    value: 'record_integrity',
    label: 'Record Integrity',
    description: 'Find contradictions, unsupported claims, and citation problems.',
    accent: '#ef4444',
    tone: 'rgba(239,68,68,0.14)',
  },
  {
    value: 'procedural_risk',
    label: 'Procedural Risk',
    description: 'Check limitation, jurisdiction, sequencing, and filing readiness.',
    accent: '#f59e0b',
    tone: 'rgba(245,158,11,0.14)',
  },
  {
    value: 'client_business_exposure',
    label: 'Client / Business Exposure',
    description: 'Pressure-test assumptions, advice posture, and business impact.',
    accent: '#22c55e',
    tone: 'rgba(34,197,94,0.14)',
  },
];

const DEFAULT_LENSES = LENS_OPTIONS.map(item => item.value);

const MODE_OPTIONS = [
  {
    value: 'matter',
    label: 'Full Matter',
    description: 'Stress-test the entire Sabha matter, uploaded record, and retained knowledge.',
    icon: Sword,
  },
  {
    value: 'draft',
    label: 'Draft',
    description: 'Attack a selected drafting file and show where the position could break.',
    icon: FileText,
  },
  {
    value: 'issue',
    label: 'Issue',
    description: 'Red-team one issue like limitation, jurisdiction, maintainability, or relief.',
    icon: Target,
  },
];

const AGGRESSIVENESS_OPTIONS = [
  {
    value: 'conservative',
    label: 'Conservative',
    description: 'Only show vulnerabilities that are tightly grounded in the supplied record.',
  },
  {
    value: 'standard',
    label: 'Standard',
    description: 'Balance breadth and rigor for a realistic pre-filing stress test.',
  },
  {
    value: 'brutal',
    label: 'Brutal',
    description: 'Push harder and surface plausible weaknesses with clear uncertainty labels.',
  },
];

const DESTINATION_OPTIONS = [
  { value: 'risk_register', label: 'Risk register' },
  { value: 'open_questions', label: 'Open questions' },
  { value: 'hearing_pack', label: 'Hearing pack' },
  { value: 'research_queue', label: 'Research queue' },
  { value: 'client_follow_up', label: 'Client follow-up' },
  { value: 'team_task_list', label: 'Team task list' },
  { value: 'matter_notes', label: 'Matter notes' },
];

const DESTINATION_LABELS = Object.fromEntries(DESTINATION_OPTIONS.map(item => [item.value, item.label]));

const PATCH_BUCKET_META = {
  top_critical_fixes: {
    label: 'Top Critical Fixes',
    icon: AlertTriangle,
    accent: '#ef4444',
    tone: 'rgba(239,68,68,0.14)',
  },
  research_queue: {
    label: 'Research Queue',
    icon: Search,
    accent: '#38bdf8',
    tone: 'rgba(56,189,248,0.14)',
  },
  client_questions: {
    label: 'Client Questions',
    icon: MessageSquare,
    accent: '#22c55e',
    tone: 'rgba(34,197,94,0.14)',
  },
  team_questions: {
    label: 'Team Questions',
    icon: Sword,
    accent: '#a78bfa',
    tone: 'rgba(167,139,250,0.14)',
  },
  draft_revisions: {
    label: 'Draft Revisions',
    icon: FileText,
    accent: '#f97316',
    tone: 'rgba(249,115,22,0.14)',
  },
  hearing_prep_upgrades: {
    label: 'Hearing Prep Upgrades',
    icon: FolderKanban,
    accent: '#f59e0b',
    tone: 'rgba(245,158,11,0.14)',
  },
};

const SEVERITY_META = {
  critical: { label: 'Critical', color: '#ef4444', tone: 'rgba(239,68,68,0.14)' },
  high: { label: 'High', color: '#f97316', tone: 'rgba(249,115,22,0.14)' },
  medium: { label: 'Medium', color: '#f59e0b', tone: 'rgba(245,158,11,0.14)' },
  low: { label: 'Low', color: '#60a5fa', tone: 'rgba(96,165,250,0.14)' },
};

const CONFIDENCE_META = {
  certain: { label: 'Certain', color: '#22c55e', tone: 'rgba(34,197,94,0.14)' },
  possible: { label: 'Possible', color: '#f59e0b', tone: 'rgba(245,158,11,0.14)' },
  inferred: { label: 'Inferred', color: '#38bdf8', tone: 'rgba(56,189,248,0.14)' },
  needs_review: { label: 'Needs review', color: 'var(--text-secondary)', tone: 'rgba(148,163,184,0.14)' },
};

const REVIEW_META = {
  new: { label: 'New', color: 'var(--text-secondary)', tone: 'rgba(148,163,184,0.14)' },
  needs_review: { label: 'Needs review', color: '#f59e0b', tone: 'rgba(245,158,11,0.14)' },
  needs_follow_up: { label: 'Needs follow-up', color: '#f59e0b', tone: 'rgba(245,158,11,0.18)' },
  reviewed: { label: 'Reviewed', color: '#60a5fa', tone: 'rgba(96,165,250,0.14)' },
  accepted: { label: 'Accepted', color: '#22c55e', tone: 'rgba(34,197,94,0.14)' },
  rejected: { label: 'Rejected', color: '#ef4444', tone: 'rgba(239,68,68,0.14)' },
};

const CATEGORY_META = {
  factual: { label: 'Factual', color: '#60a5fa', tone: 'rgba(96,165,250,0.14)' },
  legal: { label: 'Legal', color: '#a78bfa', tone: 'rgba(167,139,250,0.14)' },
  procedural: { label: 'Procedural', color: '#f59e0b', tone: 'rgba(245,158,11,0.14)' },
  evidentiary: { label: 'Evidentiary', color: '#ef4444', tone: 'rgba(239,68,68,0.14)' },
  strategic: { label: 'Strategic', color: '#22c55e', tone: 'rgba(34,197,94,0.14)' },
  business: { label: 'Business', color: '#14b8a6', tone: 'rgba(20,184,166,0.14)' },
};

const SCOPE_META = {
  matter: { label: 'Matter', color: '#60a5fa', tone: 'rgba(96,165,250,0.14)' },
  draft: { label: 'Draft', color: '#f97316', tone: 'rgba(249,115,22,0.14)' },
  issue: { label: 'Issue', color: '#22c55e', tone: 'rgba(34,197,94,0.14)' },
};

const READINESS_META = {
  green: { label: 'Ready', color: '#22c55e', tone: 'rgba(34,197,94,0.14)' },
  amber: { label: 'Watch', color: '#f59e0b', tone: 'rgba(245,158,11,0.14)' },
  red: { label: 'Blockers', color: '#ef4444', tone: 'rgba(239,68,68,0.14)' },
};

const GUARDRAIL_META = {
  clear: { label: 'Clear', color: '#22c55e', tone: 'rgba(34,197,94,0.14)' },
  watch: { label: 'Watch', color: '#f59e0b', tone: 'rgba(245,158,11,0.14)' },
  at_risk: { label: 'At risk', color: '#ef4444', tone: 'rgba(239,68,68,0.14)' },
  unknown: { label: 'Unknown', color: 'var(--text-secondary)', tone: 'rgba(148,163,184,0.14)' },
};

const PRAYER_META = {
  workable: { label: 'Workable', color: '#22c55e', tone: 'rgba(34,197,94,0.14)' },
  watch: { label: 'Watch', color: '#f59e0b', tone: 'rgba(245,158,11,0.14)' },
  fragile: { label: 'Fragile', color: '#ef4444', tone: 'rgba(239,68,68,0.14)' },
};

const PRIMARY_BUTTON_CLASS = 'px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors';
const SECONDARY_BUTTON_CLASS = 'px-4 py-2 border border-gray-300 hover:bg-gray-50 text-gray-700 text-sm font-medium rounded-lg transition-colors';
const GHOST_BUTTON_CLASS = 'px-4 py-2 hover:bg-gray-100 text-gray-600 text-sm font-medium rounded-lg transition-colors';

function buildGuidedAinaPrompt(title, items = [], guidance = '') {
  const cleanedItems = (items || []).map(item => String(item || '').trim()).filter(Boolean).slice(0, 8);
  return [
    `Guide me through this Aina section: ${title}.`,
    cleanedItems.length ? `Current points:\n- ${cleanedItems.join('\n- ')}` : '',
    guidance || 'Start with the most important unresolved point, ask me one question at a time, and move to the next highest-value step only after we resolve the current one.',
  ].filter(Boolean).join('\n\n');
}

function formatDateTime(value) {
  if (!value) return 'Just now';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatRelativeTime(value) {
  if (!value) return 'Just now';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  const diff = Date.now() - parsed.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

function truncateText(value, maxLength = 180) {
  const clean = String(value || '').replace(/\s+/g, ' ').trim();
  if (!clean) return '';
  return clean.length > maxLength ? `${clean.slice(0, maxLength - 1).trim()}…` : clean;
}

function getMatterLabel(matterId, matters, workspace) {
  if (workspace?.matter?.matter_id === matterId) {
    return workspace.matter.title || workspace.matter.name || matterId;
  }
  const matter = (matters || []).find(item => item.matter_id === matterId);
  return matter?.title || matter?.name || matterId;
}

function suggestedDestinationForFinding(finding) {
  if ((finding?.severity || '').toLowerCase() === 'critical') return 'risk_register';
  if (finding?.lens === 'bench_questions') return 'hearing_pack';
  if (finding?.lens === 'client_business_exposure') return 'client_follow_up';
  if (finding?.lens === 'procedural_risk') return 'risk_register';
  if (finding?.lens === 'record_integrity') return 'open_questions';
  return 'research_queue';
}

function MetaPill({ meta, children }) {
  if (!meta) return null;
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        padding: '5px 9px',
        borderRadius: '999px',
        background: meta.tone,
        color: meta.color,
        fontSize: '11px',
        fontWeight: 700,
        border: '1px solid rgba(255,255,255,0.04)',
      }}
    >
      {children || meta.label}
    </span>
  );
}

function StatTile({ label, value, sub, loading = false }) {
  return (
    <div className="advoq-card" style={{ padding: '16px', borderRadius: '18px', background: 'var(--bg-elevated)' }}>
      <div style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)', marginBottom: '8px' }}>
        {label}
      </div>
      <div style={{ color: 'var(--text-primary)', fontSize: '24px', fontWeight: 800, letterSpacing: '-0.04em', fontFamily: 'var(--font-display)', minHeight: '32px', display: 'flex', alignItems: 'center' }}>
        {loading ? <Loader2 size={18} className="animate-spin" style={{ color: 'var(--text-muted)' }} /> : value}
      </div>
      {sub ? (
        <div style={{ color: 'var(--text-secondary)', fontSize: '12px', lineHeight: 1.55, marginTop: '6px' }}>
          {loading ? 'Loading current scope...' : sub}
        </div>
      ) : null}
    </div>
  );
}

function LensToggle({ lens, active, onToggle }) {
  return (
    <button
      type="button"
      onClick={() => onToggle(lens.value)}
      className="advoq-card"
      style={{
        padding: '14px',
        borderRadius: '18px',
        textAlign: 'left',
        width: '100%',
        background: active ? lens.tone : 'var(--bg-surface)',
        border: `1px solid ${active ? lens.accent : 'var(--border-subtle)'}`,
        boxShadow: active ? 'var(--app-panel-shadow-soft)' : 'none',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', marginBottom: '8px' }}>
        <div style={{ color: active ? lens.accent : 'var(--text-primary)', fontSize: '14px', fontWeight: 700 }}>
          {lens.label}
        </div>
        {active ? <CheckCircle2 size={14} style={{ color: lens.accent, flexShrink: 0 }} /> : null}
      </div>
      <div style={{ color: 'var(--text-secondary)', fontSize: '12px', lineHeight: 1.6 }}>
        {lens.description}
      </div>
    </button>
  );
}

function PatchBucketCard({ bucketKey, items, onStartWithAina = null }) {
  const meta = PATCH_BUCKET_META[bucketKey] || PATCH_BUCKET_META.top_critical_fixes;
  const Icon = meta.icon;
  return (
    <div className="advoq-card" style={{ padding: '18px', borderRadius: '20px', background: 'var(--bg-elevated)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
        <span
          style={{
            width: '34px',
            height: '34px',
            borderRadius: '12px',
            background: meta.tone,
            color: meta.accent,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Icon size={16} />
        </span>
        <div>
          <div style={{ color: 'var(--text-primary)', fontSize: '15px', fontWeight: 700 }}>{meta.label}</div>
          <div style={{ color: 'var(--text-muted)', fontSize: '11px' }}>{items.length} item{items.length === 1 ? '' : 's'}</div>
        </div>
      </div>
      {items.length ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {items.map((item, index) => (
            <button
              type="button"
              key={`${bucketKey}_${index}`}
              onClick={() => onStartWithAina?.({ bucketKey, item })}
              style={{
                padding: '12px 13px',
                borderRadius: '14px',
                border: '1px solid var(--border-subtle)',
                background: 'var(--bg-surface)',
                color: 'var(--text-secondary)',
                fontSize: '13px',
                lineHeight: 1.65,
                textAlign: 'left',
                cursor: onStartWithAina ? 'pointer' : 'default',
              }}
            >
              {item}
            </button>
          ))}
        </div>
      ) : (
        <div style={{ color: 'var(--text-muted)', fontSize: '13px', lineHeight: 1.65 }}>
          No items in this bucket yet.
        </div>
      )}
    </div>
  );
}

function getAinaConversationStorageKey(matterId, runId) {
  return `aina_conversation_state_${matterId || 'global'}_${runId || 'latest'}`;
}

function loadStoredAinaConversation(matterId, runId) {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(getAinaConversationStorageKey(matterId, runId));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function saveStoredAinaConversation(matterId, runId, payload) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(
      getAinaConversationStorageKey(matterId, runId),
      JSON.stringify({ ...payload, saved_at: new Date().toISOString() }),
    );
  } catch {}
}

function savePendingAinaEdit(payload) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(
      AINA_PENDING_EDIT_STORAGE_KEY,
      JSON.stringify({ ...payload, saved_at: new Date().toISOString() }),
    );
  } catch {}
}

function buildAinaPath({ matterId = '', runId = '', threadId = '', openChat = false } = {}) {
  const params = new URLSearchParams();
  if (runId) params.set('run_id', runId);
  if (threadId) params.set('thread_id', threadId);
  if (openChat) params.set('open_chat', '1');
  const basePath = matterId ? `/aina/${matterId}` : '/aina';
  return `${basePath}${params.toString() ? `?${params.toString()}` : ''}`;
}

function normalizeAinaAssistantContent(value) {
  const raw = String(value || '').trim();
  if (!raw) return '';

  let normalized = raw
    .replace(/\r\n/g, '\n')
    .replace(/\*\*(.*?)\*\*/g, '$1')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n');

  normalized = normalized
    .replace(/^##\s*Current Bucket:\s*/im, '## Current Focus\n')
    .replace(/^###\s*Next Most Useful Question:\s*/im, '## Next Question\n')
    .replace(/^###\s*What Proof Would Resolve This:\s*/im, '## What Would Resolve This\n')
    .replace(/^###\s*What I Still Need:\s*/im, '## What I Still Need\n')
    .replace(/^###\s*Best Next Step:\s*/im, '## Best Next Step\n');

  normalized = normalized
    .replace(/(^|\n)\#\#\#?\s*/g, '$1## ')
    .replace(/(^|\n)(\d+\.)\*\*/g, '$1$2 ')
    .replace(/\*\*Prompt:\s*/gi, 'Prompt: ')
    .replace(/\*\*When /g, 'When ')
    .replace(/\*\*What /g, 'What ')
    .replace(/\*\*Who /g, 'Who ')
    .replace(/\*\*Mr\./g, 'Mr.');

  const hasHeading = /^#{1,6}\s+/m.test(normalized);
  if (!hasHeading) {
    normalized = `## Aina Guidance\n\n${normalized}`;
  }

  return normalized.trim();
}

function RunHistoryCard({ run, active, onSelect }) {
  const criticalCount = Number(run?.severity_counts?.critical || 0);
  const highCount = Number(run?.severity_counts?.high || 0);

  return (
    <button
      type="button"
      onClick={() => onSelect(run.run_id)}
      className="advoq-card"
      style={{
        width: '100%',
        textAlign: 'left',
        padding: '14px',
        borderRadius: '18px',
        background: active ? 'var(--accent-glow)' : 'var(--bg-elevated)',
        border: `1px solid ${active ? 'var(--accent-primary)' : 'var(--border-subtle)'}`,
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', alignItems: 'flex-start' }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ color: 'var(--text-primary)', fontSize: '14px', fontWeight: 700, lineHeight: 1.4 }}>
            {run.scope_label || run.draft_title || run.matter_name || 'Aina run'}
          </div>
          <div style={{ color: 'var(--text-muted)', fontSize: '11px', marginTop: '5px' }}>
            {String(run.mode || 'matter').replace('_', ' ')} · {String(run.aggressiveness || 'standard')}
          </div>
        </div>
        <span style={{ color: 'var(--text-muted)', fontSize: '11px', flexShrink: 0 }}>
          {formatRelativeTime(run.created_at)}
        </span>
      </div>

      <div style={{ color: 'var(--text-secondary)', fontSize: '12px', lineHeight: 1.6 }}>
        {truncateText(run.summary || 'Aina completed an adversarial pass for this scope.', 140)}
      </div>

      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
        <MetaPill meta={SEVERITY_META.critical}>{criticalCount} critical</MetaPill>
        <MetaPill meta={SEVERITY_META.high}>{highCount} high</MetaPill>
        <MetaPill meta={CONFIDENCE_META.possible}>{run.finding_count || 0} findings</MetaPill>
      </div>
    </button>
  );
}

function PatchActionCard({ action, onExecute, onAskAina, onSendToResearch, onApplyEdit, executing = false, finding = null }) {
  const bucketMeta = PATCH_BUCKET_META[action.plan_bucket] || PATCH_BUCKET_META.top_critical_fixes;
  const statusMeta = action.status === 'executed'
    ? { label: 'Executed', color: '#22c55e', tone: 'rgba(34,197,94,0.14)' }
    : { label: 'Pending', color: '#f59e0b', tone: 'rgba(245,158,11,0.14)' };

  return (
    <div className="advoq-card" style={{ padding: '16px', borderRadius: '18px', background: 'var(--bg-elevated)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '8px' }}>
            <MetaPill meta={{ label: bucketMeta.label, color: bucketMeta.accent, tone: bucketMeta.tone }} />
            <MetaPill meta={statusMeta} />
          </div>
          <div style={{ color: 'var(--text-primary)', fontSize: '15px', fontWeight: 700, lineHeight: 1.45 }}>
            {action.title}
          </div>
        </div>
        <div style={{ color: 'var(--text-muted)', fontSize: '11px' }}>
          {String(action.priority || 'medium').toUpperCase()}
        </div>
      </div>
      <div style={{ color: 'var(--text-secondary)', fontSize: '13px', lineHeight: 1.65, marginTop: '10px' }}>
        {action.description}
      </div>
      <div style={{ marginTop: '12px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
        <button type="button" className="app-chat-button" onClick={() => onExecute(action)} disabled={executing}>
          {executing ? <Loader2 size={12} className="animate-spin" /> : <ArrowRight size={12} />}
          Execute
        </button>
        <button type="button" className="app-chat-button" onClick={() => onAskAina(action)}>
          <MessageSquare size={12} /> Ask Aina
        </button>
        <button type="button" className="app-chat-button" onClick={() => onSendToResearch(action)}>
          <Search size={12} /> Research
        </button>
        {finding?.recommended_patch ? (
          <button type="button" className="app-chat-button" onClick={() => onApplyEdit?.(finding.finding_id, finding.recommended_patch, finding.title)}>
            <FileText size={12} /> Apply edit
          </button>
        ) : null}
      </div>
    </div>
  );
}

function SecondaryInfoCard({ eyebrow, title, action, children, style = {} }) {
  return (
    <section
      className="advoq-card"
      style={{
        padding: '18px',
        borderRadius: '22px',
        background: 'color-mix(in srgb, var(--bg-elevated) 62%, transparent)',
        border: '1px solid color-mix(in srgb, var(--border-default) 78%, transparent)',
        ...style,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'center', marginBottom: '14px', flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--accent-primary)', marginBottom: '6px' }}>
            {eyebrow}
          </div>
          <h3 style={{ margin: 0, color: 'var(--text-primary)', fontSize: '18px' }}>
            {title}
          </h3>
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

function FindingCard({
  finding,
  matterId,
  destination,
  onDestinationChange,
  onReview,
  onSave,
  onPreview,
  onSendToResearch,
  onApplyEdit,
  saving,
  reviewing,
  matterLinked,
}) {
  const isMobile = useIsMobile();
  const severity = SEVERITY_META[finding.severity] || SEVERITY_META.medium;
  const confidence = CONFIDENCE_META[finding.confidence_label] || CONFIDENCE_META.needs_review;
  const review = REVIEW_META[finding.collaborationReviewState || finding.review_status] || REVIEW_META.new;
  const category = CATEGORY_META[finding.category] || CATEGORY_META.strategic;
  const scope = SCOPE_META[finding.affected_scope] || SCOPE_META.matter;
  const lens = LENS_OPTIONS.find(item => item.value === finding.lens) || LENS_OPTIONS[0];
  const savedDestinations = new Set(finding.saved_destinations || []);

  return (
    <div className="advoq-card" style={{ padding: '20px', borderRadius: '22px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '14px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div style={{ maxWidth: '840px' }}>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '10px' }}>
            <MetaPill meta={severity} />
            <MetaPill meta={confidence} />
            <MetaPill meta={review} />
            <MetaPill meta={category} />
            <MetaPill meta={scope} />
            <MetaPill meta={{ label: lens.label, color: lens.accent, tone: lens.tone }} />
          </div>
          <h3 style={{ margin: 0, color: 'var(--text-primary)', fontSize: '20px', lineHeight: 1.28 }}>
            {finding.title}
          </h3>
        </div>
        <div style={{ color: 'var(--text-muted)', fontSize: '12px', whiteSpace: 'nowrap' }}>
          Updated {formatDateTime(finding.updated_at)}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(250px, 1fr))', gap: '14px', marginTop: '16px' }}>
        <div style={{ padding: '14px', borderRadius: '16px', border: '1px solid var(--border-subtle)', background: 'var(--bg-elevated)' }}>
          <div style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.08em', color: lens.accent, marginBottom: '7px' }}>
            Why Aina flagged this
          </div>
          <div style={{ color: 'var(--text-secondary)', fontSize: '13px', lineHeight: 1.7 }}>
            {finding.explanation}
          </div>
        </div>
        <div style={{ padding: '14px', borderRadius: '16px', border: '1px solid var(--border-subtle)', background: 'var(--bg-elevated)' }}>
          <div style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.08em', color: '#f59e0b', marginBottom: '7px' }}>
            Why this matters
          </div>
          <div style={{ color: 'var(--text-secondary)', fontSize: '13px', lineHeight: 1.7 }}>
            {finding.why_this_matters}
          </div>
        </div>
        <div style={{ padding: '14px', borderRadius: '16px', border: '1px solid var(--border-subtle)', background: 'var(--bg-elevated)' }}>
          <div style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.08em', color: '#ef4444', marginBottom: '7px' }}>
            Adversarial framing
          </div>
          <div style={{ color: 'var(--text-secondary)', fontSize: '13px', lineHeight: 1.7 }}>
            {finding.adversarial_framing}
          </div>
        </div>
      </div>

      {finding.review_status === 'accepted' || finding.review_status === 'rejected' ? (
        <div
          style={{
            marginTop: '14px',
            padding: '11px 14px',
            borderRadius: '14px',
            background: review.tone,
            color: review.color,
            border: `1px solid ${review.color}22`,
            fontSize: '12px',
            fontWeight: 700,
            letterSpacing: '0.02em',
          }}
        >
          {finding.review_status === 'accepted' ? 'Accepted for matter use' : 'Rejected and kept out of the trusted patch path'}
        </div>
      ) : null}

      <div style={{ marginTop: '14px', padding: '15px 16px', borderRadius: '18px', background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.16)' }}>
        <div style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.08em', color: '#22c55e', marginBottom: '7px' }}>
          Recommended patch
        </div>
        <div style={{ color: 'var(--text-primary)', fontSize: '14px', lineHeight: 1.7 }}>
          {finding.recommended_patch}
        </div>
      </div>

      {/* Live Redline — Proposed Draft section */}
      {finding.recommended_patch && (
        <div style={{ marginTop: '12px', borderRadius: '18px', border: '1px solid rgba(251,191,36,0.28)', overflow: 'hidden' }}>
          <div style={{ padding: '10px 16px', background: 'rgba(251,191,36,0.08)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.08em', color: '#f59e0b', fontWeight: 700 }}>
                Live Redline — Proposed Draft
              </div>
              <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>
                Aina&apos;s suggested revision. Click Apply Edit to merge this into your draft.
              </div>
            </div>
            <button
              type="button"
              onClick={() => onApplyEdit?.(finding.finding_id, finding.recommended_patch, finding.title)}
              className={`${PRIMARY_BUTTON_CLASS} inline-flex items-center gap-1.5`}
              style={{
                flexShrink: 0,
                fontSize: '12px',
                fontWeight: 700,
              }}
            >
              <ArrowRight size={12} /> Apply Edit
            </button>
          </div>
          <div style={{ padding: '14px 16px', background: 'var(--bg-surface)' }}>
            <pre style={{
              margin: 0,
              fontFamily: 'var(--font-ui)',
              fontSize: '13px',
              color: 'var(--text-primary)',
              lineHeight: 1.7,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}>
              {finding.recommended_patch}
            </pre>
          </div>
        </div>
      )}

      <div style={{ marginTop: '16px' }}>
        <div style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)', marginBottom: '10px' }}>
          Supporting citations
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '12px' }}>
          {(finding.supporting_citations || []).map((citation, index) => (
            <div
              key={`${finding.finding_id}_citation_${index}`}
              style={{
                padding: '14px',
                borderRadius: '16px',
                border: '1px solid var(--border-subtle)',
                background: 'var(--bg-elevated)',
                display: 'flex',
                flexDirection: 'column',
                gap: '10px',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', alignItems: 'flex-start' }}>
                <div>
                  <div style={{ color: 'var(--text-primary)', fontSize: '13px', fontWeight: 700, lineHeight: 1.4 }}>
                    {citation.title || citation.label || 'Source'}
                  </div>
                  <div style={{ color: 'var(--text-muted)', fontSize: '11px', marginTop: '4px' }}>
                    {citation.label || DESTINATION_LABELS[citation.sourceType] || 'Source'}
                  </div>
                </div>
                {citation.previewUrl ? (
                  <button
                    type="button"
                    className="app-chat-button"
                    onClick={() => onPreview(citation.previewUrl, citation.title || citation.label || 'Source')}
                  >
                    <Eye size={12} /> Open
                  </button>
                ) : null}
              </div>
              <div style={{ color: 'var(--text-secondary)', fontSize: '12px', lineHeight: 1.65 }}>
                {citation.quote || citation.snippet}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', gap: '12px', alignItems: isMobile ? 'stretch' : 'center', flexWrap: 'wrap', marginTop: '18px' }}>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', flex: '1 1 420px' }}>
          <div style={{ minWidth: isMobile ? '100%' : '420px', flex: '1 1 420px' }}>
            <CollaborationStrip
              matterId={matterId}
              targetType="aina_finding"
              targetId={finding.finding_id}
              targetLabel={finding.title}
              initialState={finding.collaboration || { review_state: finding.collaborationReviewState || finding.review_status }}
              fallbackReviewState={finding.collaborationReviewState || finding.review_status || 'new'}
              onReviewChange={(nextState) => onReview(finding.finding_id, nextState)}
              disabled={reviewing}
              compact
            />
          </div>
          <button type="button" className="app-chat-button" onClick={() => onSendToResearch(finding)}>
            <Search size={12} /> Send to Research
          </button>
        </div>

        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
          <select
            className="advoq-input"
            value={destination}
            disabled={!matterLinked}
            onChange={event => onDestinationChange(finding.finding_id, event.target.value)}
            style={{ minWidth: '168px' }}
          >
            {DESTINATION_OPTIONS.map(option => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
          <button
            type="button"
            className="advoq-btn-primary"
            onClick={() => onSave(finding.finding_id, destination)}
            disabled={saving || !matterLinked}
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <ArrowRight size={14} />}
            {savedDestinations.has(destination) ? 'Saved again' : 'Save to matter'}
          </button>
        </div>
      </div>

      {!matterLinked ? (
        <div style={{ marginTop: '12px', color: 'var(--text-muted)', fontSize: '12px', lineHeight: 1.6 }}>
          Link this run to a matter if you want to save findings back into Sabha intelligence.
        </div>
      ) : null}
    </div>
  );
}

export default function AinaWorkbench({
  embedded = false,
  initialMatterId = '',
  initialMatterName = '',
  initialWorkspace = null,
  initialRunId = '',
  initialThreadId = '',
  initialOpenChat = false,
  initialPrompt = '',
  onPreview = null,
  onScrollToTop = null,
  onViewportLockChange = null,
}) {
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  const [matters, setMatters] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [selectedMatterId, setSelectedMatterId] = useState(initialMatterId || initialWorkspace?.matter?.matter_id || '');
  const [selectedMatterName, setSelectedMatterName] = useState(initialMatterName || initialWorkspace?.matter?.title || initialWorkspace?.matter?.name || '');
  const [selectedMode, setSelectedMode] = useState(initialMatterId ? 'matter' : 'matter');
  const [selectedDraftDocId, setSelectedDraftDocId] = useState('');
  const [issueText, setIssueText] = useState('');
  const [selectedLenses, setSelectedLenses] = useState(DEFAULT_LENSES);
  const [aggressiveness, setAggressiveness] = useState('standard');
  const [loadingMatters, setLoadingMatters] = useState(false);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [loadingRuns, setLoadingRuns] = useState(false);
  const [loadingRunDetails, setLoadingRunDetails] = useState(false);
  const [running, setRunning] = useState(false);
  const [matterWorkspace, setMatterWorkspace] = useState(initialWorkspace || null);
  const [runs, setRuns] = useState(initialWorkspace?.ainaRuns || []);
  const [activeRun, setActiveRun] = useState(null);
  const [destinationByFinding, setDestinationByFinding] = useState({});
  const [savingFindingId, setSavingFindingId] = useState('');
  const [reviewingFindingId, setReviewingFindingId] = useState('');
  const [executingPatchActionId, setExecutingPatchActionId] = useState('');
  const [internalPreviewUrl, setInternalPreviewUrl] = useState('');
  const [internalPreviewTitle, setInternalPreviewTitle] = useState('');
  const [secondaryOpen, setSecondaryOpen] = useState(false);
  const [showMatterInfo, setShowMatterInfo] = useState(false);
  const [showSetup, setShowSetup] = useState(false);
  const matterInfoRef = useRef(null);
  // Interactive mirror chat
  const [ainaChatMessages, setAinaChatMessages] = useState([]);
  const [ainaChatInput, setAinaChatInput] = useState('');
  const [ainaChatStreaming, setAinaChatStreaming] = useState(false);
  const [showAinaChat, setShowAinaChat] = useState(false);
  const [pendingAinaPrompt, setPendingAinaPrompt] = useState('');
  const [ainaThreads, setAinaThreads] = useState([]);
  const [activeAinaThreadId, setActiveAinaThreadId] = useState(initialThreadId || '');
  const ainaChatEndRef = useRef(null);
  const ainaChatSectionRef = useRef(null);
  const ainaChatListRef = useRef(null);
  const ainaChatAbortRef = useRef(null);
  const loadAinaThreadsRef = useRef(async () => []);

  useEffect(() => {
    if (!showMatterInfo) return;
    const handler = (e) => {
      if (matterInfoRef.current && !matterInfoRef.current.contains(e.target)) {
        setShowMatterInfo(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showMatterInfo]);

  const previewSource = onPreview || ((url, title = '') => {
    setInternalPreviewUrl(url);
    setInternalPreviewTitle(title);
  });

  const currentMatter = useMemo(() => {
    if (matterWorkspace?.matter?.matter_id === selectedMatterId) return matterWorkspace.matter;
    return matters.find(item => item.matter_id === selectedMatterId) || null;
  }, [matterWorkspace, matters, selectedMatterId]);

  const selectedDraft = useMemo(
    () => documents.find(item => item.doc_id === selectedDraftDocId) || null,
    [documents, selectedDraftDocId],
  );

  const groupedFindings = useMemo(() => {
    const findings = activeRun?.findings || [];
    return LENS_OPTIONS.map(lens => ({
      ...lens,
      findings: findings.filter(item => item.lens === lens.value),
    })).filter(group => group.findings.length);
  }, [activeRun]);

  const criticalFindings = useMemo(
    () => (activeRun?.findings || []).filter(item => item.severity === 'critical').slice(0, 4),
    [activeRun],
  );
  const reviewCounts = useMemo(() => {
    const counts = { accepted: 0, rejected: 0, reviewed: 0, needs_review: 0 };
    (activeRun?.findings || []).forEach((item) => {
      const key = item.review_status || 'needs_review';
      counts[key] = (counts[key] || 0) + 1;
    });
    return counts;
  }, [activeRun]);
  const findingById = useMemo(
    () => Object.fromEntries((activeRun?.findings || []).map((item) => [item.finding_id, item])),
    [activeRun],
  );

  const severityCounts = activeRun?.severity_counts || {};
  const scopeCounts = matterWorkspace?.sources?.counts || {};
  const matterSummary = matterWorkspace?.intelligence?.overview || currentMatter?.description || '';
  const advancedAnalysis = activeRun?.advanced_analysis || {};
  const filingReadiness = advancedAnalysis.filing_readiness || {};
  const proceduralGuardrails = advancedAnalysis.procedural_guardrails || [];
  const prayerAttack = advancedAnalysis.prayer_attack || {};
  const burdenMatrix = advancedAnalysis.burden_matrix || [];
  const contradictionMap = advancedAnalysis.contradiction_map || [];
  const hearingStrategy = advancedAnalysis.hearing_strategy || {};
  const benchSimulations = advancedAnalysis.bench_simulations || [];
  const opponentStrategy = advancedAnalysis.opponent_strategy || {};
  const postHearingRecovery = advancedAnalysis.post_hearing_recovery || {};
  const clientRisk = advancedAnalysis.client_risk || {};
  const multilingualSupport = advancedAnalysis.multilingual_support || {};

  const syncDestinationsFromRun = useCallback((runPayload) => {
    const nextMap = {};
    (runPayload?.findings || []).forEach((finding) => {
      nextMap[finding.finding_id] = (finding.saved_destinations || [])[0] || suggestedDestinationForFinding(finding);
    });
    setDestinationByFinding(nextMap);
  }, []);

  const loadRunDetails = useCallback(async (runId) => {
    if (!runId) return;
    setLoadingRunDetails(true);
    try {
      const resp = await fetch(`${API}/aina/runs/${runId}`, {
        credentials: 'include',
        headers: getAuthHeaders(),
      });
      if (!resp.ok) throw new Error('load failed');
      const payload = await resp.json();
      setActiveRun(payload);
      setShowSetup(false);
      syncDestinationsFromRun(payload);
      loadAinaThreadsRef.current({ matterId: payload.matter_id || selectedMatterId, runId });
      if (payload.matter_id && payload.matter_id !== selectedMatterId) {
        setSelectedMatterId(payload.matter_id);
        setSelectedMatterName(payload.matter_name || '');
      }
    } catch {
      toast.error('Unable to load this Aina run');
    } finally {
      setLoadingRunDetails(false);
    }
  }, [getAuthHeaders, selectedMatterId, syncDestinationsFromRun]);

  const loadMatters = useCallback(async () => {
    setLoadingMatters(true);
    try {
      const resp = await fetch(`${API}/matters`, {
        credentials: 'include',
        headers: getAuthHeaders(),
      });
      if (!resp.ok) throw new Error('load failed');
      const data = await resp.json();
      setMatters(data || []);
    } catch {
      toast.error('Unable to load matters for Aina');
    } finally {
      setLoadingMatters(false);
    }
  }, [getAuthHeaders]);

  const loadDocuments = useCallback(async () => {
    setLoadingDocuments(true);
    try {
      const resp = await fetch(`${API}/documents`, {
        credentials: 'include',
        headers: getAuthHeaders(),
      });
      if (!resp.ok) throw new Error('load failed');
      const data = await resp.json();
      setDocuments(data || []);
      if (!selectedDraftDocId && data?.length) {
        setSelectedDraftDocId(data[0].doc_id);
      }
    } catch {
      toast.error('Unable to load drafting files');
    } finally {
      setLoadingDocuments(false);
    }
  }, [getAuthHeaders, selectedDraftDocId]);

  const loadGlobalRuns = useCallback(async () => {
    setLoadingRuns(true);
    try {
      const resp = await fetch(`${API}/aina/runs`, {
        credentials: 'include',
        headers: getAuthHeaders(),
      });
      if (!resp.ok) throw new Error('load failed');
      const data = await resp.json();
      setRuns(data || []);
    } catch {
      toast.error('Unable to load recent Aina runs');
    } finally {
      setLoadingRuns(false);
    }
  }, [getAuthHeaders]);

  const loadAinaThreads = useCallback(async ({ matterId, runId } = {}) => {
    const params = new URLSearchParams();
    if (matterId) params.set('matter_id', matterId);
    if (runId) params.set('run_id', runId);
    try {
      const resp = await fetch(`${API}/aina/threads${params.toString() ? `?${params.toString()}` : ''}`, {
        credentials: 'include',
        headers: getAuthHeaders(),
      });
      if (!resp.ok) throw new Error('load failed');
      const payload = await resp.json();
      setAinaThreads(Array.isArray(payload) ? payload : []);
      return Array.isArray(payload) ? payload : [];
    } catch {
      setAinaThreads([]);
      return [];
    }
  }, [getAuthHeaders]);
  loadAinaThreadsRef.current = loadAinaThreads;

  const loadAinaThread = useCallback(async (threadId) => {
    if (!threadId) return null;
    try {
      const resp = await fetch(`${API}/aina/threads/${threadId}`, {
        credentials: 'include',
        headers: getAuthHeaders(),
      });
      if (!resp.ok) throw new Error('load failed');
      const payload = await resp.json();
      setActiveAinaThreadId(payload.thread_id || threadId);
      setAinaChatMessages(payload.messages || []);
      if (typeof window !== 'undefined') {
        const params = new URLSearchParams(window.location.search);
        params.set('thread_id', payload.thread_id || threadId);
        if (payload.run_id) params.set('run_id', payload.run_id);
        const basePath = payload.matter_id ? `/aina/${payload.matter_id}` : '/aina';
        window.history.replaceState(null, '', `${basePath}?${params.toString()}`);
      }
      if (payload.run_id && payload.run_id !== activeRun?.run_id) {
        loadRunDetails(payload.run_id);
      }
      return payload;
    } catch {
      return null;
    }
  }, [activeRun, getAuthHeaders, loadRunDetails]);

  const loadMatterContext = useCallback(async (matterId, { preserveRun = false } = {}) => {
    if (!matterId) {
      setMatterWorkspace(null);
      setRuns([]);
      if (!preserveRun) setActiveRun(null);
      return;
    }

    setLoadingRuns(true);
    try {
      const [workspaceResp, runsResp] = await Promise.all([
        fetch(`${API}/matters/${matterId}/workspace`, {
          credentials: 'include',
          headers: getAuthHeaders(),
        }),
        fetch(`${API}/aina/runs?matter_id=${encodeURIComponent(matterId)}`, {
          credentials: 'include',
          headers: getAuthHeaders(),
        }),
      ]);

      if (!workspaceResp.ok) throw new Error('workspace failed');
      const workspacePayload = await workspaceResp.json();
      const runPayload = runsResp.ok ? await runsResp.json() : [];

      setMatterWorkspace(workspacePayload);
      setRuns(runPayload || []);
      setSelectedMatterName(
        workspacePayload?.matter?.title
        || workspacePayload?.matter?.name
        || getMatterLabel(matterId, matters, workspacePayload)
        || ''
      );

      if (!preserveRun) {
        if ((runPayload || []).length) {
          loadRunDetails(runPayload[0].run_id);
        } else {
          setActiveRun(null);
        }
      }
      loadAinaThreads({ matterId, runId: (runPayload || [])[0]?.run_id || '' });
    } catch {
      toast.error('Unable to load this matter for Aina');
    } finally {
      setLoadingRuns(false);
    }
  }, [getAuthHeaders, loadAinaThreads, loadRunDetails, matters]);

  useEffect(() => {
    loadMatters();
    loadDocuments();
  }, [loadDocuments, loadMatters]);

  const loadedMatterIdRef = useRef('');

  useEffect(() => {
    if (!selectedMatterId) {
      loadedMatterIdRef.current = '';
      setMatterWorkspace(null);
      if (!initialRunId) {
        setActiveRun(null);
        loadGlobalRuns();
      }
      return;
    }
    if (loadedMatterIdRef.current === selectedMatterId) {
      return;
    }
    loadedMatterIdRef.current = selectedMatterId;
    loadMatterContext(selectedMatterId);
  }, [initialRunId, loadGlobalRuns, loadMatterContext, selectedMatterId]);

  useEffect(() => {
    if (initialWorkspace?.matter?.matter_id === selectedMatterId) {
      setMatterWorkspace(initialWorkspace);
      setRuns(initialWorkspace?.ainaRuns || []);
    }
  }, [initialWorkspace, selectedMatterId]);

  useEffect(() => {
    if (initialRunId) {
      loadRunDetails(initialRunId);
    }
  }, [initialRunId, loadRunDetails]);

  useEffect(() => {
    if (initialThreadId) {
      loadAinaThread(initialThreadId);
    }
  }, [initialThreadId, loadAinaThread]);

  useEffect(() => {
    if (!initialOpenChat && !initialPrompt) return;
    setShowAinaChat(Boolean(initialOpenChat || initialPrompt));
    if (initialPrompt) {
      setAinaChatInput(initialPrompt);
    }
  }, [initialOpenChat, initialPrompt]);

  useEffect(() => {
    if (!activeAinaThreadId && ainaThreads.length > 0) {
      loadAinaThread(ainaThreads[0].thread_id);
    }
  }, [activeAinaThreadId, ainaThreads, loadAinaThread]);

  useEffect(() => {
    const matterKey = activeRun?.matter_id || selectedMatterId;
    const runKey = activeRun?.run_id || initialRunId;
    const stored = loadStoredAinaConversation(matterKey, runKey);
    if (!stored) return;
    if (!activeAinaThreadId && Array.isArray(stored.messages) && stored.messages.length > 0) {
      setAinaChatMessages(stored.messages);
    }
    if (!activeAinaThreadId && stored.thread_id) {
      setActiveAinaThreadId(stored.thread_id);
    }
  }, [activeAinaThreadId, activeRun?.matter_id, activeRun?.run_id, initialRunId, selectedMatterId]);

  useEffect(() => {
    const matterKey = activeRun?.matter_id || selectedMatterId;
    const runKey = activeRun?.run_id || initialRunId;
    if (!matterKey && !runKey && !ainaChatMessages.length) return;
    saveStoredAinaConversation(matterKey, runKey, {
      thread_id: activeAinaThreadId,
      messages: ainaChatMessages,
    });
  }, [activeAinaThreadId, activeRun?.matter_id, activeRun?.run_id, ainaChatMessages, initialRunId, selectedMatterId]);

  const toggleLens = lensValue => {
    setSelectedLenses(prev => {
      if (prev.includes(lensValue)) {
        const next = prev.filter(item => item !== lensValue);
        return next.length ? next : DEFAULT_LENSES;
      }
      return [...prev, lensValue];
    });
  };

  const selectAllLenses = () => setSelectedLenses(DEFAULT_LENSES);

  const runAina = async () => {
    if (selectedMode !== 'draft' && !selectedMatterId) {
      toast.error('Select a matter for full-matter or issue analysis');
      return;
    }
    if (selectedMode === 'draft' && !selectedDraftDocId) {
      toast.error('Select a draft to stress-test');
      return;
    }
    if (selectedMode === 'issue' && !issueText.trim()) {
      toast.error('Describe the issue you want Aina to attack');
      return;
    }

    setRunning(true);
    try {
      // Build extra matter context so Aina can catch strategy inconsistencies
      const matterContextParts = [];
      if (currentMatter?.description) matterContextParts.push(`Description: ${currentMatter.description}`);
      if (currentMatter?.practice_area) matterContextParts.push(`Practice area: ${currentMatter.practice_area}`);
      if (currentMatter?.court_forum) matterContextParts.push(`Court/forum: ${currentMatter.court_forum}`);
      if (currentMatter?.opposing_party) matterContextParts.push(`Opposing party: ${currentMatter.opposing_party}`);
      if (currentMatter?.case_number) matterContextParts.push(`Case number: ${currentMatter.case_number}`);
      if (currentMatter?.internal_notes) matterContextParts.push(`Internal notes: ${currentMatter.internal_notes.slice(0, 400)}`);
      const extraContext = matterContextParts.length ? matterContextParts.join('\n') : null;

      const resp = await fetch(`${API}/aina/runs`, {
        method: 'POST',
        credentials: 'include',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          matter_id: selectedMatterId || null,
          matter_name: currentMatter?.title || currentMatter?.name || selectedMatterName || null,
          workspace_id: matterWorkspace?.workspace_id || initialWorkspace?.workspace_id || null,
          mode: selectedMode,
          draft_doc_id: selectedMode === 'draft' ? selectedDraftDocId : null,
          issue_text: selectedMode === 'issue' ? issueText.trim() : null,
          lenses: selectedLenses,
          aggressiveness,
          extra_context: extraContext,
        }),
      });
      if (!resp.ok) throw new Error('run failed');
      const payload = await resp.json();
      setActiveRun(payload);
      setShowSetup(false);
      syncDestinationsFromRun(payload);
      setActiveAinaThreadId('');
      setAinaChatMessages([]);
      setRuns(prev => [payload, ...prev.filter(item => item.run_id !== payload.run_id)].slice(0, 20));
      loadAinaThreads({ matterId: payload.matter_id || selectedMatterId, runId: payload.run_id });
      if (payload.matter_id) {
        loadMatterContext(payload.matter_id, { preserveRun: true });
      }
      toast.success('Aina finished the adversarial pass');
      window.sentinelReportActivity?.('run_complete');
    } catch {
      toast.error('Aina could not complete this run');
    } finally {
      setRunning(false);
    }
  };

  const updateFindingReview = async (findingId, reviewStatus) => {
    setReviewingFindingId(findingId);
    try {
      const resp = await fetch(`${API}/aina/findings/${findingId}/review`, {
        method: 'PUT',
        credentials: 'include',
        headers: getAuthHeaders(),
        body: JSON.stringify({ review_status: reviewStatus }),
      });
      if (!resp.ok) throw new Error('review failed');
      const updatedFinding = await resp.json();
      setActiveRun(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          findings: (prev.findings || []).map(item => (
            item.finding_id === findingId
              ? {
                  ...item,
                  review_status: updatedFinding.review_status,
                  collaborationReviewState: updatedFinding.collaboration?.review_state || updatedFinding.review_status,
                  collaboration: updatedFinding.collaboration || item.collaboration,
                  updated_at: updatedFinding.updated_at,
                }
              : item
          )),
        };
      });
      toast.success(`Finding marked ${REVIEW_META[reviewStatus]?.label?.toLowerCase() || reviewStatus}`);
      return updatedFinding.collaboration || { review_state: updatedFinding.review_status };
    } catch {
      toast.error('Unable to update review state');
      throw new Error('Unable to update review state');
    } finally {
      setReviewingFindingId('');
    }
  };

  const saveFinding = async (findingId, destination) => {
    setSavingFindingId(findingId);
    try {
      const resp = await fetch(`${API}/aina/findings/${findingId}/save`, {
        method: 'POST',
        credentials: 'include',
        headers: getAuthHeaders(),
        body: JSON.stringify({ destination }),
      });
      if (!resp.ok) throw new Error('save failed');
      setActiveRun(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          findings: (prev.findings || []).map(item => (
            item.finding_id === findingId
              ? {
                  ...item,
                  review_status: 'accepted',
                  collaborationReviewState: 'accepted',
                  saved_destinations: Array.from(new Set([...(item.saved_destinations || []), destination])),
                  updated_at: new Date().toISOString(),
                }
              : item
          )),
        };
      });
      if (selectedMatterId) {
        loadMatterContext(selectedMatterId, { preserveRun: true });
      }
      toast.success(`Saved into ${DESTINATION_LABELS[destination] || 'matter intelligence'}`);
    } catch {
      toast.error('Unable to save this finding into the matter');
    } finally {
      setSavingFindingId('');
    }
  };

  const applyFindingEdit = (findingId, patch, title) => {
    if (!patch) return;
    const targetDocId = activeRun?.draft_doc_id || selectedDraftDocId || '';
    const editPayload = { findingId, patch, title, doc_id: targetDocId };
    savePendingAinaEdit(editPayload);
    navigator.clipboard?.writeText(patch).catch(() => {});
    window.dispatchEvent(new CustomEvent('aina-apply-edit', {
      detail: editPayload,
    }));
    if (targetDocId && !window.location.pathname.startsWith(`/documents/${targetDocId}`)) {
      navigate(`/documents/${targetDocId}`);
    }
    toast.success('Edit applied — patch copied to clipboard and ready to merge', {
      description: title ? `"${title.slice(0, 60)}"` : undefined,
      duration: 5000,
    });
  };

  const buildCurrentAinaReturnPath = useCallback((overrides = {}) => buildAinaPath({
    matterId: overrides.matterId ?? activeRun?.matter_id ?? selectedMatterId,
    runId: overrides.runId ?? activeRun?.run_id ?? initialRunId,
    threadId: overrides.threadId ?? activeAinaThreadId ?? initialThreadId,
    openChat: overrides.openChat ?? showAinaChat,
  }), [activeAinaThreadId, activeRun?.matter_id, activeRun?.run_id, initialRunId, initialThreadId, selectedMatterId, showAinaChat]);


  const openResearchFromFinding = useCallback((finding) => {
    const query = finding.recommended_patch || finding.title || 'Research this Aina finding';
    const params = new URLSearchParams();
    params.set('prefill_query', query);
    params.set('prefill_type', 'TOPIC');
    params.set('return_to', 'aina');
    params.set('aina_return_path', buildCurrentAinaReturnPath({ openChat: true }));
    if (selectedMatterId) {
      params.set('matter_id', selectedMatterId);
      params.set('matter_name', currentMatter?.title || currentMatter?.name || selectedMatterName || selectedMatterId);
    }
    navigate(`/research/agent?${params.toString()}`);
  }, [buildCurrentAinaReturnPath, currentMatter?.name, currentMatter?.title, navigate, selectedMatterId, selectedMatterName]);

  const appendAinaChatDictation = useCallback((text) => {
    const cleaned = String(text || '').trim();
    if (!cleaned) return;
    setAinaChatInput(prev => {
      const spacer = prev && !/[ \n]$/.test(prev) ? ' ' : '';
      return `${prev}${spacer}${cleaned}`.trimStart();
    });
  }, []);

  const focusAinaChat = useCallback(() => {
    if (typeof window !== 'undefined') {
      window.requestAnimationFrame(() => {
        ainaChatSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  }, []);

  const startAinaGuidedPrompt = useCallback((prompt, { autoSend = false } = {}) => {
    const cleaned = String(prompt || '').trim();
    if (!cleaned) return;
    setShowAinaChat(true);
    setAinaChatInput(cleaned);
    if (autoSend) {
      setPendingAinaPrompt(cleaned);
    }
    focusAinaChat();
  }, [focusAinaChat]);

  const askAinaAboutPatchAction = useCallback((action) => {
    const prompt = [
      'Guide me through resolving this Aina patch action inside the current matter.',
      `Action: ${action.title}`,
      `Description: ${action.description}`,
      `Destination: ${action.destination_hint || 'matter_notes'}`,
      'Start with the single highest-value next question, wait for my answer, and then move me to the next unresolved prompt once this one is covered.',
    ].join('\n');
    startAinaGuidedPrompt(prompt, { autoSend: true });
  }, [startAinaGuidedPrompt]);

  const askAinaAboutReadiness = useCallback(() => {
    startAinaGuidedPrompt(
      buildGuidedAinaPrompt(
        'Filing readiness',
        [
          filingReadiness.headline,
          ...(filingReadiness.blockers || []).map(item => `${item.title}: ${item.next_step || item.why_it_matters}`),
        ],
        'Start with the blocker most likely to hurt filing readiness, tell me what proof closes it fastest, and then move me through the rest one by one.',
      ),
      { autoSend: true },
    );
  }, [filingReadiness, startAinaGuidedPrompt]);

  const askAinaAboutHearing = useCallback(() => {
    startAinaGuidedPrompt(
      buildGuidedAinaPrompt(
        'Bench simulation and hearing strategy',
        [
          hearingStrategy.opening_frame,
          ...(hearingStrategy.first_questions || []),
          ...(hearingStrategy.unsafe_points || []),
        ],
        'Run this like a live bench drill. Start with the first likely judicial interruption, wait for my answer, critique it, and then move to the next question.',
      ),
      { autoSend: true },
    );
  }, [hearingStrategy, startAinaGuidedPrompt]);

  const askAinaAboutProofGaps = useCallback(() => {
    startAinaGuidedPrompt(
      buildGuidedAinaPrompt(
        'Burden matrix and proof gaps',
        burdenMatrix.map(item => `${item.issue}: ${item.missing_proof || item.next_best_step}`),
        'Start with the issue where the burden is most exposed. Tell me the exact proof or document that would strengthen it, and then move to the next issue.',
      ),
      { autoSend: true },
    );
  }, [burdenMatrix, startAinaGuidedPrompt]);

  const askAinaAboutRecovery = useCallback(() => {
    startAinaGuidedPrompt(
      buildGuidedAinaPrompt(
        'Post-hearing recovery',
        [
          ...(postHearingRecovery.bench_focus || []),
          ...(postHearingRecovery.documents_to_file || []),
          ...(postHearingRecovery.authorities_to_find || []),
        ],
        'Turn this into an immediate post-hearing action plan. Start with the most urgent filing or authority task, wait for my response, and then continue.',
      ),
      { autoSend: true },
    );
  }, [postHearingRecovery, startAinaGuidedPrompt]);

  const runPatchAction = useCallback(async (action) => {
    if (!action?.patch_action_id) return;
    setExecutingPatchActionId(action.patch_action_id);
    try {
      const resp = await fetch(`${API}/aina/patch-actions/${action.patch_action_id}/execute`, {
        method: 'POST',
        credentials: 'include',
        headers: getAuthHeaders(),
        body: JSON.stringify({ destination: action.destination_hint }),
      });
      if (!resp.ok) {
        let detail = 'execute failed';
        try {
          const payload = await resp.json();
          detail = payload?.detail || payload?.message || detail;
        } catch {}
        throw new Error(detail);
      }
      const payload = await resp.json();
      setActiveRun(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          patch_actions: (prev.patch_actions || []).map((item) => (
            item.patch_action_id === action.patch_action_id ? { ...item, ...(payload.patch_action || {}), status: 'executed' } : item
          )),
        };
      });
      if (selectedMatterId) {
        loadMatterContext(selectedMatterId, { preserveRun: true });
      }
      toast.success(`Patch action executed into ${DESTINATION_LABELS[payload.destination] || 'matter context'}`);
    } catch (error) {
      toast.error(error?.message || 'Unable to execute this patch action');
    } finally {
      setExecutingPatchActionId('');
    }
  }, [getAuthHeaders, loadMatterContext, selectedMatterId]);

  const sendPatchActionToResearch = useCallback((action) => {
    openResearchFromFinding({
      recommended_patch: action.description || action.title,
      title: action.title,
    });
  }, [openResearchFromFinding]);

  // Aina interactive mirror chat
  const sendAinaChat = async (messageOverride = '') => {
    const text = String(messageOverride || ainaChatInput).trim();
    if (!text || ainaChatStreaming) return;
    if (ainaChatAbortRef.current) ainaChatAbortRef.current.abort();

    const userMsg = { role: 'user', content: text };
    const assistantMsg = { role: 'assistant', content: '', isStreaming: true };
    setAinaChatMessages(prev => [...prev, userMsg, assistantMsg]);
    setAinaChatInput('');
    setAinaChatStreaming(true);

    const controller = new AbortController();
    ainaChatAbortRef.current = controller;

    try {
      const resp = await fetch(`${API}/aina/threads/stream`, {
        method: 'POST',
        credentials: 'include',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          thread_id: activeAinaThreadId || null,
          run_id: activeRun?.run_id || null,
          matter_id: selectedMatterId || null,
          matter_name: currentMatter?.title || currentMatter?.name || selectedMatterName || null,
          workspace_id: matterWorkspace?.workspace_id || initialWorkspace?.workspace_id || null,
          message: text,
        }),
        signal: controller.signal,
      });
      if (!resp.ok) throw new Error('Aina chat failed');

      let fullText = '';
      await readEventStream(resp, async (data) => {
        if (data.type === 'thread_id' && data.thread_id) {
          setActiveAinaThreadId(data.thread_id);
          loadAinaThreads({ matterId: selectedMatterId || activeRun?.matter_id || '', runId: activeRun?.run_id || '' });
          if (typeof window !== 'undefined') {
            const params = new URLSearchParams(window.location.search);
            params.set('thread_id', data.thread_id);
            if (activeRun?.run_id) params.set('run_id', activeRun.run_id);
            const basePath = activeRun?.matter_id || selectedMatterId ? `/aina/${activeRun?.matter_id || selectedMatterId}` : '/aina';
            window.history.replaceState(null, '', `${basePath}?${params.toString()}`);
          }
        }
        if (data.type === 'text') {
          fullText += data.text;
          setAinaChatMessages(prev => {
            const updated = [...prev];
            updated[updated.length - 1] = { ...updated[updated.length - 1], content: fullText };
            return updated;
          });
        }
        if (data.type === 'done') {
          setAinaChatMessages(prev => {
            const updated = [...prev];
            updated[updated.length - 1] = { ...updated[updated.length - 1], isStreaming: false };
            return updated;
          });
        }
      });
    } catch (err) {
      if (err.name !== 'AbortError') {
        toast.error('Aina chat failed. Please try again.');
        setAinaChatMessages(prev => {
          const updated = [...prev];
          updated[updated.length - 1] = { ...updated[updated.length - 1], isStreaming: false, content: 'Failed to respond. Please try again.' };
          return updated;
        });
      }
    } finally {
      setAinaChatStreaming(false);
    }
  };

  useEffect(() => {
    if (!pendingAinaPrompt || ainaChatStreaming || loadingRunDetails) return;
    const prompt = pendingAinaPrompt;
    setPendingAinaPrompt('');
    sendAinaChat(prompt);
  }, [ainaChatStreaming, loadingRunDetails, pendingAinaPrompt]);

  const startBucketGuidedPrompt = useCallback(({ bucketKey, item }) => {
    const bucketMeta = PATCH_BUCKET_META[bucketKey] || PATCH_BUCKET_META.top_critical_fixes;
    const prompt = [
      'Guide me through this Aina matter prompt step by step.',
      `Bucket: ${bucketMeta.label}`,
      `Prompt: ${item}`,
      'Ask me the next most useful question, tell me what proof or draft change would resolve it, and once we have enough coverage, move me to the next unresolved Aina prompt in this matter.',
    ].join('\n');
    startAinaGuidedPrompt(prompt, { autoSend: true });
  }, [startAinaGuidedPrompt]);

  React.useEffect(() => {
    const node = ainaChatListRef.current;
    if (!node) return;
    const distanceFromBottom = node.scrollHeight - node.scrollTop - node.clientHeight;
    const shouldStickToBottom = distanceFromBottom < 80;
    if (!shouldStickToBottom) return;
    const behavior = ainaChatStreaming ? 'auto' : 'smooth';
    window.requestAnimationFrame(() => {
      node.scrollTo({ top: node.scrollHeight, behavior });
    });
  }, [ainaChatMessages, ainaChatStreaming]);

  const topStats = [
    {
      label: 'Critical',
      value: severityCounts.critical || 0,
      sub: 'Immediate filing or advisory pressure points.',
      loading: loadingRunDetails,
    },
    {
      label: 'High Risk',
      value: severityCounts.high || 0,
      sub: 'Likely bench or opposing-counsel attacks.',
      loading: loadingRunDetails,
    },
    {
      label: 'Filing Score',
      value: activeRun ? (filingReadiness.score ?? '—') : '—',
      sub: activeRun
        ? `${(READINESS_META[filingReadiness.status] || READINESS_META.red).label} based on Aina's current filing-readiness pass.`
        : 'Run Aina to generate filing readiness.',
      loading: loadingRunDetails,
    },
    {
      label: 'Matter Sources',
      value: (scopeCounts.document || 0) + (scopeCounts.jolly || 0) + (scopeCounts.aina || 0) + (scopeCounts.legal_source || 0) + (scopeCounts.note || 0),
      sub: selectedMatterId ? `${scopeCounts.document || 0} docs · ${scopeCounts.jolly || 0} Jolly · ${scopeCounts.aina || 0} Aina` : 'Select a matter for full source context.',
      loading: Boolean(selectedMatterId) && loadingRuns && !matterWorkspace,
    },
    {
      label: 'Draft Files',
      value: documents.length,
      sub: loadingDocuments ? 'Loading drafting files...' : 'Available for draft-mode stress tests.',
      loading: loadingDocuments && documents.length === 0,
    },
  ];

  const shellPadding = embedded ? '0' : '0';
  const showMinimalLanding = !embedded && !activeRun && !loadingRunDetails;
  const revealPanelId = 'aina-secondary-reveal';
  const matterContextLabel = currentMatter?.title || currentMatter?.name || selectedMatterName || selectedMatterId;
  const selectedMatterSummary = selectedMatterId && matterSummary
    ? `${matterContextLabel || 'Selected matter'}: ${truncateText(matterSummary, 280)}`
    : '';
  const historyContent = runs.length ? (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      {runs.map(run => (
        <RunHistoryCard
          key={run.run_id}
          run={run}
          active={activeRun?.run_id === run.run_id}
          onSelect={loadRunDetails}
        />
      ))}
    </div>
  ) : (
    <div style={{ color: 'var(--text-muted)', fontSize: '13px', lineHeight: 1.7 }}>
      No Aina runs yet for this context.
    </div>
  );
  const quickPaths = [
    'Send a vulnerability straight into the Research queue.',
    'Save bench-facing issues into the Hearing Pack.',
    'Move client-risk items into follow-up before advice goes out.',
    'Keep accepted findings retrievable in matter chat with citations.',
  ];

  useEffect(() => {
    if (!onViewportLockChange) return undefined;
    onViewportLockChange(showMinimalLanding && !secondaryOpen);
    return () => onViewportLockChange(false);
  }, [onViewportLockChange, secondaryOpen, showMinimalLanding]);

  return (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', gap: showMinimalLanding ? '14px' : '18px', padding: shellPadding }}>
        {showMinimalLanding ? (
          <section
            className="advoq-card"
            style={{
              position: 'relative',
              overflow: 'hidden',
              padding: 'clamp(14px, 2vw, 22px)',
              borderRadius: '28px',
              background: [
                'radial-gradient(circle at top center, rgba(255,255,255,0.14), transparent 34%)',
                'radial-gradient(circle at 18% 18%, rgba(56,189,248,0.10), transparent 24%)',
                'linear-gradient(180deg, color-mix(in srgb, var(--bg-elevated) 96%, white 4%), color-mix(in srgb, var(--bg-surface) 92%, black 8%))',
              ].join(', '),
              border: '1px solid color-mix(in srgb, var(--border-default) 88%, rgba(255,255,255,0.18))',
              boxShadow: '0 32px 72px rgba(15, 23, 42, 0.16), inset 0 1px 0 rgba(255,255,255,0.16)',
            }}
          >
            <div
              aria-hidden="true"
              style={{
                position: 'absolute',
                inset: '1px',
                borderRadius: '31px',
                background: 'linear-gradient(140deg, rgba(255,255,255,0.14), transparent 28%, transparent 72%, rgba(255,255,255,0.08))',
                pointerEvents: 'none',
              }}
            />
            <div
              aria-hidden="true"
              style={{
                position: 'absolute',
                top: '-14%',
                left: '8%',
                width: '64%',
                height: '34%',
                borderRadius: '999px',
                background: 'linear-gradient(180deg, rgba(255,255,255,0.20), rgba(255,255,255,0.02))',
                filter: 'blur(18px)',
                opacity: 0.55,
                pointerEvents: 'none',
                transform: 'rotate(-5deg)',
              }}
            />
            <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px', textAlign: 'center' }}>
              <div style={{ maxWidth: '680px' }}>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '4px 10px', borderRadius: '999px', background: 'rgba(255,255,255,0.08)', color: 'var(--text-secondary)', marginBottom: '10px', border: '1px solid color-mix(in srgb, var(--border-default) 80%, rgba(255,255,255,0.18))', backdropFilter: 'blur(10px)' }}>
                  <Eye size={13} />
                  <span style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700 }}>
                    Premium adversarial mirror
                  </span>
                </div>
                <h2 style={{ margin: 0, color: 'var(--text-primary)', fontSize: 'clamp(1.45rem, 2.6vw, 2.2rem)', lineHeight: 1.02, fontFamily: 'var(--font-display)', letterSpacing: '-0.04em' }}>
                  Your Aina
                </h2>
                <p style={{ margin: '6px auto 0', color: 'var(--text-secondary)', fontSize: '13px', lineHeight: 1.55, maxWidth: '460px' }}>
                  Stand in front of the mirror. Run Aina.
                </p>
              </div>

              <section
                className="advoq-card"
                style={{
                  position: 'relative',
                  width: '100%',
                  maxWidth: '980px',
                  padding: 'clamp(10px, 1.6vw, 16px)',
                  borderRadius: '26px',
                  background: [
                    'linear-gradient(180deg, rgba(255,255,255,0.16), rgba(255,255,255,0.04))',
                    'linear-gradient(135deg, color-mix(in srgb, var(--bg-elevated) 88%, white 12%), color-mix(in srgb, var(--bg-surface) 90%, black 10%))',
                  ].join(', '),
                  border: '1px solid color-mix(in srgb, rgba(255,255,255,0.24) 48%, var(--border-default))',
                  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.26), inset 0 -1px 0 rgba(255,255,255,0.03), 0 24px 64px rgba(15,23,42,0.18)',
                  backdropFilter: 'blur(18px)',
                  textAlign: 'left',
                }}
              >
                <div
                  aria-hidden="true"
                  style={{
                    position: 'absolute',
                    inset: '1px',
                    borderRadius: '29px',
                    background: 'linear-gradient(120deg, rgba(255,255,255,0.13), transparent 26%, transparent 74%, rgba(255,255,255,0.06))',
                    pointerEvents: 'none',
                  }}
                />
                <div
                  aria-hidden="true"
                  style={{
                    position: 'absolute',
                    top: '10px',
                    left: '6%',
                    width: '44%',
                    height: '16px',
                    borderRadius: '999px',
                    background: 'linear-gradient(90deg, rgba(255,255,255,0.34), rgba(255,255,255,0.02))',
                    filter: 'blur(8px)',
                    opacity: 0.8,
                    pointerEvents: 'none',
                  }}
                />
                <div style={{ position: 'relative' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', alignItems: 'center', marginBottom: '8px', flexWrap: 'wrap' }}>
                    <div>
                      <div style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)', marginBottom: '4px' }}>
                        Mirror setup
                      </div>
                      <h3 style={{ margin: 0, color: 'var(--text-primary)', fontSize: 'clamp(1.18rem, 1.65vw, 1.45rem)', fontFamily: 'var(--font-display)' }}>
                        Choose what Aina should confront
                      </h3>
                    </div>
                    <button type="button" className="app-chat-button" onClick={selectAllLenses} style={{ background: 'rgba(255,255,255,0.08)', backdropFilter: 'blur(8px)' }}>
                      <Sparkles size={13} /> Full mirror
                    </button>
                  </div>

                  {!embedded || !initialMatterId ? (
                    <div style={{ marginBottom: '8px', position: 'relative' }} ref={matterInfoRef}>
                      <div style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)', marginBottom: '5px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        Matter context
                        <button
                          type="button"
                          onClick={() => setShowMatterInfo(v => !v)}
                          className={`${GHOST_BUTTON_CLASS} inline-flex items-center px-2 py-1`}
                          style={{ display: 'flex', alignItems: 'center', background: 'none', border: 'none', padding: '2px', cursor: 'pointer', color: showMatterInfo ? 'var(--accent-primary)' : 'var(--text-muted)', borderRadius: '4px' }}
                          title="What is a matter?"
                        >
                          <Info size={13} />
                        </button>
                      </div>
                      {showMatterInfo && (
                        <div style={{
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          zIndex: 200,
                          width: 'min(320px, 90vw)',
                          background: 'var(--bg-elevated)',
                          border: '1px solid var(--border-default)',
                          borderRadius: '14px',
                          padding: '14px 16px',
                          boxShadow: '0 12px 32px rgba(0,0,0,0.22)',
                          marginTop: '4px',
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                            <span style={{ fontSize: '12px', fontWeight: 700, color: 'var(--text-primary)' }}>What is a Matter?</span>
                            <button
                              type="button"
                              onClick={() => setShowMatterInfo(false)}
                              className={`${GHOST_BUTTON_CLASS} px-2 py-1`}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: '0 2px', lineHeight: 1 }}
                            >
                              ✕
                            </button>
                          </div>
                          <p style={{ margin: '0 0 8px', fontSize: '12px', color: 'var(--text-secondary)', lineHeight: 1.55 }}>
                            A <strong style={{ color: 'var(--text-primary)' }}>matter</strong> is a case, dispute, or client file you're working on — with its own documents, notes, and intelligence held in Sabha.
                          </p>
                          <p style={{ margin: '0 0 10px', fontSize: '12px', color: 'var(--text-secondary)', lineHeight: 1.55 }}>
                            Linking a matter lets Aina draw on that full context — uploaded records, saved research, and your notes — when running its analysis.
                          </p>
                          <button
                            type="button"
                            onClick={() => { setShowMatterInfo(false); navigate('/war-room'); }}
                            className={`${SECONDARY_BUTTON_CLASS} w-full`}
                            style={{ fontSize: '12px', fontWeight: 600 }}
                          >
                            Create a matter in Sabha →
                          </button>
                        </div>
                      )}
                      <select
                        className="advoq-input"
                        value={selectedMatterId}
                        onChange={event => {
                          const nextMatterId = event.target.value;
                          setSelectedMatterId(nextMatterId);
                          setSelectedMatterName(getMatterLabel(nextMatterId, matters, matterWorkspace));
                        }}
                        style={{ width: '100%', background: 'color-mix(in srgb, var(--bg-elevated) 44%, transparent)' }}
                        disabled={loadingMatters}
                      >
                        <option value="">No matter selected</option>
                        {matters.map(matter => (
                          <option key={matter.matter_id} value={matter.matter_id}>
                            {matter.title || matter.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  ) : null}

                  <div className="aina-minimal-grid aina-minimal-grid--triple" style={{ display: 'grid', gap: '10px' }}>
                    {MODE_OPTIONS.map(mode => {
                      const Icon = mode.icon;
                      const active = selectedMode === mode.value;
                      return (
                        <button
                          key={mode.value}
                          type="button"
                          onClick={() => setSelectedMode(mode.value)}
                          className="advoq-card"
                          style={{
                            padding: '12px',
                            borderRadius: '18px',
                            textAlign: 'left',
                            background: active ? 'linear-gradient(180deg, color-mix(in srgb, var(--accent-glow) 82%, rgba(255,255,255,0.08)), rgba(255,255,255,0.06))' : 'color-mix(in srgb, var(--bg-surface) 72%, rgba(255,255,255,0.08))',
                            border: `1px solid ${active ? 'var(--accent-primary)' : 'color-mix(in srgb, var(--border-subtle) 84%, rgba(255,255,255,0.14))'}`,
                            boxShadow: active ? '0 18px 36px rgba(15,23,42,0.12)' : 'inset 0 1px 0 rgba(255,255,255,0.06)',
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                            <span
                              style={{
                                width: '30px',
                                height: '30px',
                                borderRadius: '10px',
                                background: active ? 'rgba(255,255,255,0.14)' : 'rgba(255,255,255,0.06)',
                                color: active ? 'var(--accent-primary)' : 'var(--text-secondary)',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              <Icon size={16} />
                            </span>
                            <div style={{ color: 'var(--text-primary)', fontSize: '14px', fontWeight: 700 }}>{mode.label}</div>
                          </div>
                          <div style={{ color: 'var(--text-secondary)', fontSize: '11px', lineHeight: 1.5 }}>
                            {mode.description}
                          </div>
                        </button>
                      );
                    })}
                  </div>

                  {selectedMode === 'draft' ? (
                    <div style={{ marginTop: '12px' }}>
                      <div style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)', marginBottom: '6px' }}>
                        Draft selection
                      </div>
                      <select
                        className="advoq-input"
                        value={selectedDraftDocId}
                        onChange={event => setSelectedDraftDocId(event.target.value)}
                        style={{ width: '100%', background: 'color-mix(in srgb, var(--bg-elevated) 44%, transparent)' }}
                        disabled={loadingDocuments}
                      >
                        <option value="">Choose a draft</option>
                        {documents.map(document => (
                          <option key={document.doc_id} value={document.doc_id}>
                            {(document.title || 'Untitled draft')} · {String(document.type || 'Draft').replaceAll('_', ' ')}
                          </option>
                        ))}
                      </select>
                      {selectedDraft ? (
                        <div style={{ marginTop: '8px', color: 'var(--text-secondary)', fontSize: '11px', lineHeight: 1.5 }}>
                          {truncateText(selectedDraft.content || '', 220) || 'Structured draft content available for review.'}
                        </div>
                      ) : null}
                    </div>
                  ) : null}

                  {selectedMode === 'issue' ? (
                    <div style={{ marginTop: '12px' }}>
                      <div style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)', marginBottom: '6px' }}>
                        Issue statement
                      </div>
                      <textarea
                        className="advoq-input"
                        rows={4}
                        value={issueText}
                        onChange={event => setIssueText(event.target.value)}
                        placeholder="Example: limitation issue on whether the cause of action crystallized on 12 January 2024 or only after the compliance notice failed."
                        style={{ width: '100%', resize: 'vertical', background: 'color-mix(in srgb, var(--bg-elevated) 44%, transparent)' }}
                      />
                    </div>
                  ) : null}

                  <div style={{ marginTop: '10px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', alignItems: 'center', marginBottom: '6px', flexWrap: 'wrap' }}>
                      <div style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)' }}>
                        Attack lenses
                      </div>
                      <div style={{ color: 'var(--text-muted)', fontSize: '12px' }}>
                        {selectedLenses.length === DEFAULT_LENSES.length ? 'Full Aina' : `${selectedLenses.length} lens${selectedLenses.length === 1 ? '' : 'es'} selected`}
                      </div>
                    </div>
                    <div className="aina-minimal-grid aina-minimal-grid--triple" style={{ display: 'grid', gap: '10px' }}>
                      {LENS_OPTIONS.map(lens => (
                        <LensToggle key={lens.value} lens={lens} active={selectedLenses.includes(lens.value)} onToggle={toggleLens} />
                      ))}
                    </div>
                  </div>

                  <div style={{ marginTop: '10px' }}>
                    <div style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)', marginBottom: '6px' }}>
                      Aggressiveness
                    </div>
                    <div className="aina-minimal-grid aina-minimal-grid--triple" style={{ display: 'grid', gap: '10px' }}>
                      {AGGRESSIVENESS_OPTIONS.map(option => (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => setAggressiveness(option.value)}
                          className="advoq-card"
                          style={{
                            padding: '12px',
                            borderRadius: '16px',
                            textAlign: 'left',
                            background: aggressiveness === option.value ? 'linear-gradient(180deg, color-mix(in srgb, var(--accent-glow) 80%, rgba(255,255,255,0.08)), rgba(255,255,255,0.04))' : 'color-mix(in srgb, var(--bg-surface) 70%, rgba(255,255,255,0.06))',
                            border: `1px solid ${aggressiveness === option.value ? 'var(--accent-primary)' : 'color-mix(in srgb, var(--border-subtle) 84%, rgba(255,255,255,0.12))'}`,
                          }}
                        >
                          <div style={{ color: 'var(--text-primary)', fontSize: '13px', fontWeight: 700, marginBottom: '5px' }}>
                            {option.label}
                          </div>
                          <div style={{ color: 'var(--text-secondary)', fontSize: '11px', lineHeight: 1.45 }}>
                            {option.description}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <div style={{ color: 'var(--text-muted)', fontSize: '11px', lineHeight: 1.5, maxWidth: '620px' }}>
                      Aina keeps provenance intact, distinguishes strong support from inference, and writes accepted findings back into the matter when linked.
                    </div>
                    <button type="button" className="advoq-btn-primary" onClick={runAina} disabled={running}>
                      {running ? <Loader2 size={14} className="animate-spin" /> : <Eye size={14} />}
                      {selectedLenses.length === DEFAULT_LENSES.length ? 'Run Full Aina' : 'Run Aina'}
                    </button>
                  </div>
                </div>
              </section>

              <div style={{ width: '100%', maxWidth: '980px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
                <button
                  type="button"
                  className="app-chat-button"
                  aria-expanded={secondaryOpen}
                  aria-controls={revealPanelId}
                  onClick={() => setSecondaryOpen(prev => !prev)}
                  style={{
                    minHeight: '38px',
                    padding: '0 14px',
                    borderRadius: '999px',
                    background: 'color-mix(in srgb, var(--bg-elevated) 36%, transparent)',
                    border: '1px solid color-mix(in srgb, var(--border-default) 72%, rgba(255,255,255,0.18))',
                    backdropFilter: 'blur(12px)',
                    boxShadow: secondaryOpen ? '0 14px 28px rgba(15,23,42,0.10)' : 'none',
                  }}
                >
                  {secondaryOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                  Your Aina
                </button>

                <div
                  id={revealPanelId}
                  aria-hidden={!secondaryOpen}
                  style={{
                    width: '100%',
                    maxHeight: secondaryOpen ? '1800px' : '0px',
                    opacity: secondaryOpen ? 1 : 0,
                    overflow: 'hidden',
                    transform: secondaryOpen ? 'translateY(0)' : 'translateY(-10px)',
                    transition: 'max-height 320ms ease, opacity 240ms ease, transform 320ms ease',
                  }}
                >
                  <div
                    style={{
                      marginTop: '2px',
                      padding: '16px',
                      borderRadius: '24px',
                      background: 'linear-gradient(180deg, rgba(255,255,255,0.08), rgba(255,255,255,0.03))',
                      border: '1px solid color-mix(in srgb, var(--border-default) 72%, rgba(255,255,255,0.14))',
                      backdropFilter: 'blur(16px)',
                      boxShadow: '0 20px 50px rgba(15,23,42,0.12)',
                    }}
                  >
                    <div style={{ color: 'var(--text-muted)', fontSize: '12px', lineHeight: 1.7, marginBottom: '14px', textAlign: 'left' }}>
                      Recent passes, matter context, and write-back paths stay nearby without crowding the mirror.
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '12px' }}>
                      {topStats.map(stat => (
                        <StatTile key={stat.label} label={stat.label} value={stat.value} sub={stat.sub} loading={stat.loading} />
                      ))}
                    </div>
                    {selectedMatterSummary ? (
                      <div style={{ marginTop: '14px', padding: '14px 15px', borderRadius: '18px', background: 'rgba(255,255,255,0.05)', border: '1px solid var(--border-subtle)', color: 'var(--text-secondary)', fontSize: '13px', lineHeight: 1.7, textAlign: 'left' }}>
                        {selectedMatterSummary}
                      </div>
                    ) : null}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '14px', marginTop: '14px' }}>
                      <SecondaryInfoCard
                        eyebrow="Matter link"
                        title="Where Aina writes back"
                        action={selectedMatterId ? (
                          <button type="button" className="app-chat-button" onClick={() => navigate(`/war-room/${selectedMatterId}`)}>
                            <ArrowRight size={13} /> Open Sabha
                          </button>
                        ) : null}
                      >
                        <div style={{ color: 'var(--text-secondary)', fontSize: '13px', lineHeight: 1.75 }}>
                          Saved findings can flow into open questions, risk register, hearing pack, research queue, client follow-up, team tasks, and matter notes.
                        </div>
                      </SecondaryInfoCard>
                      <SecondaryInfoCard
                        eyebrow="Patch workflow"
                        title="Quick hand-off paths"
                      >
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                          {quickPaths.map((item, index) => (
                            <div
                              key={`path_${index}`}
                              style={{
                                padding: '12px 13px',
                                borderRadius: '14px',
                                border: '1px solid var(--border-subtle)',
                                background: 'var(--bg-surface)',
                                color: 'var(--text-secondary)',
                                fontSize: '13px',
                                lineHeight: 1.65,
                              }}
                            >
                              {item}
                            </div>
                          ))}
                        </div>
                      </SecondaryInfoCard>
                    </div>
                    <div style={{ marginTop: '14px' }}>
                      <SecondaryInfoCard
                        eyebrow="Run history"
                        title="Recent Aina passes"
                        action={loadingRuns ? <Loader2 size={15} className="animate-spin" style={{ color: 'var(--text-muted)' }} /> : null}
                        style={{ padding: '0', background: 'transparent', border: 'none', boxShadow: 'none' }}
                      >
                        {historyContent}
                      </SecondaryInfoCard>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        ) : (
          <section
            className="advoq-card"
            style={{
              padding: embedded ? '22px' : '26px',
              borderRadius: embedded ? '22px' : '24px',
              background: 'linear-gradient(135deg, color-mix(in srgb, #ef4444 10%, var(--bg-surface)), color-mix(in srgb, #f97316 10%, var(--bg-surface)), color-mix(in srgb, var(--bg-elevated) 92%, transparent))',
              border: '1px solid var(--border-default)',
              boxShadow: 'var(--app-panel-shadow)',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '18px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
              <div style={{ maxWidth: '860px' }}>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '6px 12px', borderRadius: '999px', background: 'rgba(255,255,255,0.08)', color: 'var(--text-secondary)', marginBottom: '14px', border: '1px solid var(--border-subtle)' }}>
                  <Eye size={14} />
                  <span style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700 }}>
                    Premium adversarial mirror
                  </span>
                </div>
                <h2 style={{ margin: 0, color: 'var(--text-primary)', fontSize: embedded ? '30px' : 'clamp(2rem, 4vw, 2.8rem)', lineHeight: 1.04, fontFamily: 'var(--font-display)' }}>
                  Aina stress-tests the matter before the outside world does.
                </h2>
                <p style={{ margin: '14px 0 0', color: 'var(--text-secondary)', fontSize: '15px', lineHeight: 1.8 }}>
                  Run Aina against a full matter, a draft, or one issue. It red-teams the record, flags vulnerabilities with provenance, and turns them into a patch plan you can save back into Sabha intelligence.
                </p>
                {selectedMatterSummary ? (
                  <div style={{ marginTop: '14px', padding: '13px 14px', borderRadius: '16px', background: 'rgba(255,255,255,0.06)', border: '1px solid var(--border-subtle)', color: 'var(--text-secondary)', fontSize: '13px', lineHeight: 1.7 }}>
                    <strong style={{ color: 'var(--text-primary)' }}>{matterContextLabel || 'Selected matter'}:</strong> {truncateText(matterSummary, 280)}
                  </div>
                ) : null}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '12px', minWidth: embedded ? '100%' : '360px', flex: embedded ? '1 1 100%' : '0 1 420px' }}>
                {topStats.map(stat => (
                  <StatTile key={stat.label} label={stat.label} value={stat.value} sub={stat.sub} loading={stat.loading} />
                ))}
              </div>
            </div>
          </section>
        )}

        <section style={{ display: 'grid', gridTemplateColumns: embedded ? 'minmax(0, 1fr)' : 'minmax(0, 1.08fr) minmax(320px, 0.92fr)', gap: '18px', alignItems: 'start' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
            {activeRun && !showMinimalLanding ? (
              <section
                className="advoq-card"
                style={{
                  padding: '12px 18px',
                  borderRadius: '20px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: '12px',
                  flexWrap: 'wrap',
                  background: 'var(--bg-elevated)',
                  border: '1px solid var(--border-subtle)',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
                  <Eye size={16} style={{ color: 'var(--accent-primary)', flexShrink: 0 }} />
                  <div style={{ minWidth: 0 }}>
                    <div style={{ color: 'var(--text-primary)', fontSize: '14px', fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {activeRun.matter_name || activeRun.scope_label || 'Aina run'}
                    </div>
                    <div style={{ color: 'var(--text-muted)', fontSize: '11px', marginTop: '2px' }}>
                      {String(activeRun.mode || 'matter').replace('_', ' ')} · {activeRun.aggressiveness || 'standard'} · {formatRelativeTime(activeRun.created_at)}
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                  <button
                    type="button"
                    className="app-chat-button"
                    onClick={() => setShowSetup(v => !v)}
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    <Sparkles size={13} />
                    {showSetup ? 'Hide setup' : 'Run again'}
                  </button>
                  <button
                    type="button"
                    className="advoq-btn-primary"
                    onClick={() => { setActiveRun(null); setShowSetup(false); }}
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    <Eye size={13} />
                    New Aina
                  </button>
                </div>
              </section>
            ) : null}
            {!showMinimalLanding && showSetup ? (
              <section className="advoq-card" style={{ padding: '20px', borderRadius: '22px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap' }}>
                <div>
                  <div style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--accent-primary)', marginBottom: '6px' }}>
                    Run setup
                  </div>
                  <h3 style={{ margin: 0, color: 'var(--text-primary)', fontSize: '20px' }}>
                    Choose the scope Aina should attack
                  </h3>
                </div>
                <button type="button" className="app-chat-button" onClick={selectAllLenses}>
                  <Sparkles size={13} /> Run full Aina
                </button>
              </div>

              {!embedded || !initialMatterId ? (
                <div style={{ marginBottom: '16px' }}>
                  <div style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)', marginBottom: '8px' }}>
                    Matter context
                  </div>
                  <select
                    className="advoq-input"
                    value={selectedMatterId}
                    onChange={event => {
                      const nextMatterId = event.target.value;
                      setSelectedMatterId(nextMatterId);
                      setSelectedMatterName(getMatterLabel(nextMatterId, matters, matterWorkspace));
                    }}
                    style={{ width: '100%' }}
                    disabled={loadingMatters}
                  >
                    <option value="">No matter selected</option>
                    {matters.map(matter => (
                      <option key={matter.matter_id} value={matter.matter_id}>
                        {matter.title || matter.name}
                      </option>
                    ))}
                  </select>
                  <div style={{ color: 'var(--text-muted)', fontSize: '12px', marginTop: '8px', lineHeight: 1.6 }}>
                    Matter-linked runs can save findings back into Sabha, hearing prep, research queue, and matter chat retrieval.
                  </div>
                </div>
              ) : (
                <div style={{ marginBottom: '16px', padding: '14px', borderRadius: '18px', background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)' }}>
                  <div style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)', marginBottom: '6px' }}>
                    Matter context
                  </div>
                  <div style={{ color: 'var(--text-primary)', fontSize: '15px', fontWeight: 700 }}>
                    {currentMatter?.title || currentMatter?.name || selectedMatterName || selectedMatterId}
                  </div>
                  <div style={{ color: 'var(--text-secondary)', fontSize: '12px', marginTop: '6px', lineHeight: 1.6 }}>
                    This Aina workspace is locked to the current Sabha matter.
                  </div>
                </div>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: '12px' }}>
                {MODE_OPTIONS.map(mode => {
                  const Icon = mode.icon;
                  const active = selectedMode === mode.value;
                  return (
                    <button
                      key={mode.value}
                      type="button"
                      onClick={() => setSelectedMode(mode.value)}
                      className="advoq-card"
                      style={{
                        padding: '16px',
                        borderRadius: '18px',
                        textAlign: 'left',
                        background: active ? 'var(--accent-glow)' : 'var(--bg-surface)',
                        border: `1px solid ${active ? 'var(--accent-primary)' : 'var(--border-subtle)'}`,
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                        <span
                          style={{
                            width: '34px',
                            height: '34px',
                            borderRadius: '12px',
                            background: active ? 'rgba(255,255,255,0.12)' : 'var(--bg-elevated)',
                            color: active ? 'var(--accent-primary)' : 'var(--text-secondary)',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <Icon size={16} />
                        </span>
                        <div style={{ color: 'var(--text-primary)', fontSize: '15px', fontWeight: 700 }}>{mode.label}</div>
                      </div>
                      <div style={{ color: 'var(--text-secondary)', fontSize: '12px', lineHeight: 1.65 }}>
                        {mode.description}
                      </div>
                    </button>
                  );
                })}
              </div>

              {selectedMode === 'draft' ? (
                <div style={{ marginTop: '16px' }}>
                  <div style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)', marginBottom: '8px' }}>
                    Draft selection
                  </div>
                  <select
                    className="advoq-input"
                    value={selectedDraftDocId}
                    onChange={event => setSelectedDraftDocId(event.target.value)}
                    style={{ width: '100%' }}
                    disabled={loadingDocuments}
                  >
                    <option value="">Choose a draft</option>
                    {documents.map(document => (
                      <option key={document.doc_id} value={document.doc_id}>
                        {(document.title || 'Untitled draft')} · {String(document.type || 'Draft').replaceAll('_', ' ')}
                      </option>
                    ))}
                  </select>
                  {selectedDraft ? (
                    <div style={{ marginTop: '10px', color: 'var(--text-secondary)', fontSize: '12px', lineHeight: 1.6 }}>
                      {truncateText(selectedDraft.content || '', 220) || 'Structured draft content available for review.'}
                    </div>
                  ) : null}
                </div>
              ) : null}

              {selectedMode === 'issue' ? (
                <div style={{ marginTop: '16px' }}>
                  <div style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)', marginBottom: '8px' }}>
                    Issue statement
                  </div>
                  <textarea
                    className="advoq-input"
                    rows={4}
                    value={issueText}
                    onChange={event => setIssueText(event.target.value)}
                    placeholder="Example: limitation issue on whether the cause of action crystallized on 12 January 2024 or only after the compliance notice failed."
                    style={{ width: '100%', resize: 'vertical' }}
                  />
                </div>
              ) : null}

              <div style={{ marginTop: '18px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'center', marginBottom: '10px', flexWrap: 'wrap' }}>
                  <div>
                    <div style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)' }}>
                      Attack lenses
                    </div>
                  </div>
                  <div style={{ color: 'var(--text-muted)', fontSize: '12px' }}>
                    {selectedLenses.length === DEFAULT_LENSES.length ? 'Full Aina' : `${selectedLenses.length} lens${selectedLenses.length === 1 ? '' : 'es'} selected`}
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '12px' }}>
                  {LENS_OPTIONS.map(lens => (
                    <LensToggle key={lens.value} lens={lens} active={selectedLenses.includes(lens.value)} onToggle={toggleLens} />
                  ))}
                </div>
              </div>

              <div style={{ marginTop: '18px' }}>
                <div style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)', marginBottom: '8px' }}>
                  Aggressiveness
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '10px' }}>
                  {AGGRESSIVENESS_OPTIONS.map(option => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setAggressiveness(option.value)}
                      className="advoq-card"
                      style={{
                        padding: '14px',
                        borderRadius: '18px',
                        textAlign: 'left',
                        background: aggressiveness === option.value ? 'var(--accent-glow)' : 'var(--bg-surface)',
                        border: `1px solid ${aggressiveness === option.value ? 'var(--accent-primary)' : 'var(--border-subtle)'}`,
                      }}
                    >
                      <div style={{ color: 'var(--text-primary)', fontSize: '14px', fontWeight: 700, marginBottom: '6px' }}>
                        {option.label}
                      </div>
                      <div style={{ color: 'var(--text-secondary)', fontSize: '12px', lineHeight: 1.6 }}>
                        {option.description}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              <div style={{ marginTop: '18px', display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
                <div style={{ color: 'var(--text-muted)', fontSize: '12px', lineHeight: 1.6, maxWidth: '760px' }}>
                  Aina will only raise supported vulnerabilities, preserve provenance, and label weaker inferences clearly as possible, inferred, or needs review.
                </div>
                <button type="button" className="advoq-btn-primary" onClick={runAina} disabled={running}>
                  {running ? <Loader2 size={14} className="animate-spin" /> : <Eye size={14} />}
                  {selectedLenses.length === DEFAULT_LENSES.length ? 'Run Full Aina' : 'Run Aina'}
                </button>
              </div>
              </section>
            ) : null}

            {loadingRunDetails ? (
              <div className="advoq-card" style={{ padding: '44px 18px', borderRadius: '22px', textAlign: 'center', color: 'var(--text-muted)' }}>
                <Loader2 size={18} className="animate-spin" style={{ margin: '0 auto 10px' }} />
                Loading adversarial findings...
              </div>
            ) : activeRun ? (
              <>
                <section className="advoq-card" style={{ padding: '20px', borderRadius: '22px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <div>
                      <div style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--accent-primary)', marginBottom: '6px' }}>
                        Latest run
                      </div>
                      <h3 style={{ margin: 0, color: 'var(--text-primary)', fontSize: '22px' }}>
                        {activeRun.scope_label || activeRun.draft_title || activeRun.matter_name || 'Aina run'}
                      </h3>
                      <p style={{ margin: '8px 0 0', color: 'var(--text-secondary)', fontSize: '14px', lineHeight: 1.75 }}>
                        {activeRun.summary}
                      </p>
                    </div>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      <MetaPill meta={SEVERITY_META.critical}>{severityCounts.critical || 0} critical</MetaPill>
                      <MetaPill meta={SEVERITY_META.high}>{severityCounts.high || 0} high</MetaPill>
                      <MetaPill meta={CONFIDENCE_META.possible}>{activeRun.finding_count || 0} findings</MetaPill>
                      <MetaPill meta={REVIEW_META.accepted}>{reviewCounts.accepted || 0} accepted</MetaPill>
                      <MetaPill meta={REVIEW_META.rejected}>{reviewCounts.rejected || 0} rejected</MetaPill>
                    </div>
                  </div>

                  {criticalFindings.length ? (
                    <div style={{ marginTop: '18px' }}>
                      <div style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.08em', color: '#ef4444', marginBottom: '10px' }}>
                        Top critical vulnerabilities
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '12px' }}>
                        {criticalFindings.map(item => (
                          <div
                            key={item.finding_id}
                            style={{
                              padding: '14px',
                              borderRadius: '16px',
                              background: 'rgba(239,68,68,0.08)',
                              border: '1px solid rgba(239,68,68,0.16)',
                            }}
                          >
                            <div style={{ color: '#ef4444', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '6px', fontWeight: 700 }}>
                              {LENS_OPTIONS.find(lens => lens.value === item.lens)?.label || 'Aina'}
                            </div>
                            <div style={{ color: 'var(--text-primary)', fontSize: '14px', fontWeight: 700, lineHeight: 1.45 }}>
                              {item.title}
                            </div>
                            <div style={{ color: 'var(--text-secondary)', fontSize: '12px', lineHeight: 1.65, marginTop: '8px' }}>
                              {truncateText(item.recommended_patch || item.explanation, 160)}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : null}
                </section>

                {Object.keys(advancedAnalysis).length ? (
                  <>
                    <section style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '14px' }}>
                      <SecondaryInfoCard
                        eyebrow="Filing OS"
                        title="Filing readiness"
                        action={filingReadiness.blockers?.length ? (
                          <button type="button" className="app-chat-button" onClick={askAinaAboutReadiness}>
                            <ArrowRight size={13} /> Close blockers
                          </button>
                        ) : null}
                      >
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
                            <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px' }}>
                              <div style={{ color: 'var(--text-primary)', fontSize: '38px', fontWeight: 800, lineHeight: 1, fontFamily: 'var(--font-display)' }}>
                                {filingReadiness.score ?? '--'}
                              </div>
                              <div style={{ color: 'var(--text-muted)', fontSize: '12px' }}>
                                out of 100
                              </div>
                            </div>
                            <MetaPill meta={READINESS_META[filingReadiness.status] || READINESS_META.red} />
                          </div>
                          <div style={{ color: 'var(--text-secondary)', fontSize: '13px', lineHeight: 1.7 }}>
                            {filingReadiness.headline || 'Aina will tell you whether this matter is safe to file or still exposed.'}
                          </div>
                          {(filingReadiness.blockers || []).length ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                              {filingReadiness.blockers.slice(0, 4).map((item, index) => (
                                <div
                                  key={`filing_blocker_${index}`}
                                  style={{
                                    padding: '12px 13px',
                                    borderRadius: '14px',
                                    border: '1px solid rgba(239,68,68,0.14)',
                                    background: 'rgba(239,68,68,0.06)',
                                  }}
                                >
                                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', alignItems: 'center', marginBottom: '6px', flexWrap: 'wrap' }}>
                                    <div style={{ color: 'var(--text-primary)', fontSize: '13px', fontWeight: 700 }}>
                                      {item.title}
                                    </div>
                                    <MetaPill meta={SEVERITY_META[item.severity] || SEVERITY_META.medium} />
                                  </div>
                                  <div style={{ color: 'var(--text-secondary)', fontSize: '12px', lineHeight: 1.65 }}>
                                    {item.next_step || item.why_it_matters}
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div style={{ color: 'var(--text-muted)', fontSize: '12px', lineHeight: 1.6 }}>
                              No major filing blockers surfaced in the current run.
                            </div>
                          )}
                          {proceduralGuardrails.length ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                              <div style={{ color: 'var(--text-muted)', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                                Procedure watchlist
                              </div>
                              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '8px' }}>
                                {proceduralGuardrails.slice(0, 5).map((item) => (
                                  <div key={item.code} style={{ padding: '10px 11px', borderRadius: '12px', background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', alignItems: 'center', marginBottom: '5px' }}>
                                      <div style={{ color: 'var(--text-primary)', fontSize: '12px', fontWeight: 700 }}>
                                        {item.label}
                                      </div>
                                      <MetaPill meta={GUARDRAIL_META[item.status] || GUARDRAIL_META.unknown} />
                                    </div>
                                    <div style={{ color: 'var(--text-secondary)', fontSize: '11px', lineHeight: 1.5 }}>
                                      {truncateText(item.summary || item.next_step, 100)}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ) : null}
                        </div>
                      </SecondaryInfoCard>

                      <SecondaryInfoCard
                        eyebrow="Hearing OS"
                        title="Bench strategy"
                        action={hearingStrategy.first_questions?.length ? (
                          <button type="button" className="app-chat-button" onClick={askAinaAboutHearing}>
                            <MessageSquare size={13} /> Run drill
                          </button>
                        ) : null}
                      >
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                          <div style={{ padding: '12px 13px', borderRadius: '14px', background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)' }}>
                            <div style={{ color: 'var(--text-muted)', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '6px' }}>
                              Opening frame
                            </div>
                            <div style={{ color: 'var(--text-primary)', fontSize: '13px', lineHeight: 1.7 }}>
                              {hearingStrategy.opening_frame || 'Aina will build an opening frame once the matter has bench-facing signal.'}
                            </div>
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '10px' }}>
                            <div style={{ padding: '12px 13px', borderRadius: '14px', background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)' }}>
                              <div style={{ color: '#38bdf8', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '6px' }}>
                                First questions
                              </div>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '7px' }}>
                                {(hearingStrategy.first_questions || []).slice(0, 3).map((item, index) => (
                                  <div key={`hearing_q_${index}`} style={{ color: 'var(--text-secondary)', fontSize: '12px', lineHeight: 1.55 }}>
                                    {item}
                                  </div>
                                ))}
                                {!hearingStrategy.first_questions?.length ? (
                                  <div style={{ color: 'var(--text-muted)', fontSize: '12px', lineHeight: 1.55 }}>
                                    No bench prompts surfaced yet.
                                  </div>
                                ) : null}
                              </div>
                            </div>
                            <div style={{ padding: '12px 13px', borderRadius: '14px', background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)' }}>
                              <div style={{ color: '#ef4444', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '6px' }}>
                                Unsafe points
                              </div>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '7px' }}>
                                {(hearingStrategy.unsafe_points || []).slice(0, 3).map((item, index) => (
                                  <div key={`unsafe_q_${index}`} style={{ color: 'var(--text-secondary)', fontSize: '12px', lineHeight: 1.55 }}>
                                    {item}
                                  </div>
                                ))}
                                {!hearingStrategy.unsafe_points?.length ? (
                                  <div style={{ color: 'var(--text-muted)', fontSize: '12px', lineHeight: 1.55 }}>
                                    Aina did not surface a major unsafe point here.
                                  </div>
                                ) : null}
                              </div>
                            </div>
                          </div>
                        </div>
                      </SecondaryInfoCard>
                    </section>

                    <section style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '14px' }}>
                      <SecondaryInfoCard
                        eyebrow="Prayer attack"
                        title="Relief pressure test"
                      >
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                          {(prayerAttack.prayers || []).slice(0, 4).map((item, index) => (
                            <div key={`prayer_${index}`} style={{ padding: '12px 13px', borderRadius: '14px', background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '6px' }}>
                                <div style={{ color: 'var(--text-primary)', fontSize: '13px', fontWeight: 700 }}>
                                  {item.prayer}
                                </div>
                                <MetaPill meta={PRAYER_META[item.viability] || PRAYER_META.watch} />
                              </div>
                              <div style={{ color: 'var(--text-secondary)', fontSize: '12px', lineHeight: 1.6 }}>
                                {item.attack_surface}
                              </div>
                              <div style={{ color: 'var(--text-muted)', fontSize: '11px', lineHeight: 1.55, marginTop: '7px' }}>
                                Fallback: {item.fallback_prayer}
                              </div>
                            </div>
                          ))}
                          {!prayerAttack.prayers?.length ? (
                            <div style={{ color: 'var(--text-muted)', fontSize: '12px', lineHeight: 1.6 }}>
                              No explicit prayer surfaced in the current scope, so Aina is still relying on issue-level attack.
                            </div>
                          ) : null}
                        </div>
                      </SecondaryInfoCard>

                      <SecondaryInfoCard
                        eyebrow="Burden matrix"
                        title="Who still has to prove what"
                        action={burdenMatrix.length ? (
                          <button type="button" className="app-chat-button" onClick={askAinaAboutProofGaps}>
                            <Search size={13} /> Close proof gaps
                          </button>
                        ) : null}
                      >
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                          {burdenMatrix.slice(0, 4).map((item, index) => (
                            <div key={`burden_${index}`} style={{ padding: '12px 13px', borderRadius: '14px', background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)' }}>
                              <div style={{ color: 'var(--text-primary)', fontSize: '13px', fontWeight: 700, marginBottom: '5px' }}>
                                {item.issue}
                              </div>
                              <div style={{ color: 'var(--text-muted)', fontSize: '11px', marginBottom: '6px' }}>
                                Burden on: {item.burden_on}
                              </div>
                              <div style={{ color: 'var(--text-secondary)', fontSize: '12px', lineHeight: 1.6 }}>
                                Missing proof: {item.missing_proof}
                              </div>
                              <div style={{ color: 'var(--text-muted)', fontSize: '11px', lineHeight: 1.55, marginTop: '7px' }}>
                                Likely attack: {item.likely_attack}
                              </div>
                            </div>
                          ))}
                          {!burdenMatrix.length ? (
                            <div style={{ color: 'var(--text-muted)', fontSize: '12px', lineHeight: 1.6 }}>
                              Aina did not generate a burden map for this run yet.
                            </div>
                          ) : null}
                        </div>
                      </SecondaryInfoCard>
                    </section>

                    <section style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '14px' }}>
                      <SecondaryInfoCard
                        eyebrow="Record integrity"
                        title="Contradiction map"
                      >
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                          {contradictionMap.slice(0, 4).map((item, index) => (
                            <div key={`contradiction_${index}`} style={{ padding: '12px 13px', borderRadius: '14px', background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.14)' }}>
                              <div style={{ color: 'var(--text-primary)', fontSize: '13px', fontWeight: 700, marginBottom: '6px' }}>
                                {item.title}
                              </div>
                              <div style={{ color: 'var(--text-secondary)', fontSize: '12px', lineHeight: 1.6 }}>
                                {item.conflict}
                              </div>
                              <div style={{ color: 'var(--text-muted)', fontSize: '11px', lineHeight: 1.55, marginTop: '7px' }}>
                                Fix: {item.resolution_path}
                              </div>
                            </div>
                          ))}
                          {!contradictionMap.length ? (
                            <div style={{ color: 'var(--text-muted)', fontSize: '12px', lineHeight: 1.6 }}>
                              No contradiction cluster surfaced beyond the main findings list.
                            </div>
                          ) : null}
                        </div>
                      </SecondaryInfoCard>

                      <SecondaryInfoCard
                        eyebrow="Opponent forecast"
                        title="What the other side attacks first"
                      >
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                          <div style={{ padding: '12px 13px', borderRadius: '14px', background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)' }}>
                            <div style={{ color: '#f97316', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '6px' }}>
                              Expected order
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '7px' }}>
                              {(opponentStrategy.expected_order || []).slice(0, 4).map((item, index) => (
                                <div key={`opp_order_${index}`} style={{ color: 'var(--text-secondary)', fontSize: '12px', lineHeight: 1.55 }}>
                                  {index + 1}. {item}
                                </div>
                              ))}
                            </div>
                          </div>
                          <div style={{ color: 'var(--text-secondary)', fontSize: '12px', lineHeight: 1.6 }}>
                            {opponentStrategy.hardest_point || 'Aina will call out the hardest opposition point when one clearly emerges.'}
                          </div>
                          {(opponentStrategy.documents_they_will_use || []).length ? (
                            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                              {opponentStrategy.documents_they_will_use.slice(0, 3).map((item, index) => (
                                <span key={`opp_doc_${index}`} style={{ padding: '7px 10px', borderRadius: '999px', background: 'rgba(239,68,68,0.10)', color: '#ef4444', fontSize: '11px', fontWeight: 700 }}>
                                  {truncateText(item, 64)}
                                </span>
                              ))}
                            </div>
                          ) : null}
                          {(opponentStrategy.pre_buttals || []).length ? (
                            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                              {opponentStrategy.pre_buttals.slice(0, 3).map((item, index) => (
                                <span key={`opp_pre_${index}`} style={{ padding: '7px 10px', borderRadius: '999px', background: 'rgba(56,189,248,0.12)', color: '#38bdf8', fontSize: '11px', fontWeight: 700 }}>
                                  {truncateText(item, 72)}
                                </span>
                              ))}
                            </div>
                          ) : null}
                        </div>
                      </SecondaryInfoCard>
                    </section>

                    <section style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '14px' }}>
                      <SecondaryInfoCard
                        eyebrow="Bench simulator"
                        title="Courtroom drill cards"
                      >
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                          {benchSimulations.slice(0, 4).map((item, index) => (
                            <button
                              type="button"
                              key={`bench_sim_${index}`}
                              onClick={() => startAinaGuidedPrompt(buildGuidedAinaPrompt(item.persona, [item.first_question, ...(item.follow_ups || []), item.suggested_answer], 'Run this as a live courtroom simulation. Ask the first question, wait for my answer, critique it, and then continue.'), { autoSend: true })}
                              style={{
                                padding: '12px 13px',
                                borderRadius: '14px',
                                border: '1px solid var(--border-subtle)',
                                background: 'var(--bg-elevated)',
                                textAlign: 'left',
                                cursor: 'pointer',
                              }}
                            >
                              <div style={{ color: 'var(--text-primary)', fontSize: '13px', fontWeight: 700, marginBottom: '6px' }}>
                                {item.persona}
                              </div>
                              <div style={{ color: 'var(--text-secondary)', fontSize: '12px', lineHeight: 1.6 }}>
                                {item.first_question}
                              </div>
                            </button>
                          ))}
                          {!benchSimulations.length ? (
                            <div style={{ color: 'var(--text-muted)', fontSize: '12px', lineHeight: 1.6 }}>
                              Run Aina on a matter with hearing signal to unlock courtroom drills.
                            </div>
                          ) : null}
                        </div>
                      </SecondaryInfoCard>

                      <SecondaryInfoCard
                        eyebrow="Post-hearing"
                        title="Recovery mode"
                        action={(postHearingRecovery.documents_to_file?.length || postHearingRecovery.authorities_to_find?.length) ? (
                          <button type="button" className="app-chat-button" onClick={askAinaAboutRecovery}>
                            <FolderKanban size={13} /> Build plan
                          </button>
                        ) : null}
                      >
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '10px' }}>
                          <div style={{ padding: '12px 13px', borderRadius: '14px', background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)' }}>
                            <div style={{ color: '#38bdf8', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '6px' }}>
                              Bench focus
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '7px' }}>
                              {(postHearingRecovery.bench_focus || []).slice(0, 3).map((item, index) => (
                                <div key={`bench_focus_${index}`} style={{ color: 'var(--text-secondary)', fontSize: '12px', lineHeight: 1.55 }}>
                                  {item}
                                </div>
                              ))}
                            </div>
                          </div>
                          <div style={{ padding: '12px 13px', borderRadius: '14px', background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)' }}>
                            <div style={{ color: '#f59e0b', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '6px' }}>
                              Documents to file
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '7px' }}>
                              {(postHearingRecovery.documents_to_file || []).slice(0, 3).map((item, index) => (
                                <div key={`docs_file_${index}`} style={{ color: 'var(--text-secondary)', fontSize: '12px', lineHeight: 1.55 }}>
                                  {item}
                                </div>
                              ))}
                            </div>
                          </div>
                          <div style={{ padding: '12px 13px', borderRadius: '14px', background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)' }}>
                            <div style={{ color: '#22c55e', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '6px' }}>
                              Authorities to find
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '7px' }}>
                              {(postHearingRecovery.authorities_to_find || []).slice(0, 3).map((item, index) => (
                                <div key={`auth_find_${index}`} style={{ color: 'var(--text-secondary)', fontSize: '12px', lineHeight: 1.55 }}>
                                  {item}
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </SecondaryInfoCard>
                    </section>

                    <section style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '14px' }}>
                      <SecondaryInfoCard
                        eyebrow="Client risk"
                        title="Advice posture and confirmations"
                      >
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                          <div style={{ color: 'var(--text-secondary)', fontSize: '12px', lineHeight: 1.6 }}>
                            {clientRisk.settlement_posture || 'Aina will surface advice caveats and client confirmations here.'}
                          </div>
                          {(clientRisk.must_confirm || []).length ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '7px' }}>
                              {clientRisk.must_confirm.slice(0, 4).map((item, index) => (
                                <div key={`client_confirm_${index}`} style={{ padding: '10px 12px', borderRadius: '12px', background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)', color: 'var(--text-secondary)', fontSize: '12px', lineHeight: 1.55 }}>
                                  {item}
                                </div>
                              ))}
                            </div>
                          ) : null}
                          {(clientRisk.advice_caveats || []).length ? (
                            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                              {clientRisk.advice_caveats.slice(0, 3).map((item, index) => (
                                <span key={`client_caveat_${index}`} style={{ padding: '7px 10px', borderRadius: '999px', background: 'rgba(245,158,11,0.12)', color: '#f59e0b', fontSize: '11px', fontWeight: 700 }}>
                                  {truncateText(item, 72)}
                                </span>
                              ))}
                            </div>
                          ) : null}
                        </div>
                      </SecondaryInfoCard>

                      <SecondaryInfoCard
                        eyebrow="Matter language"
                        title="Multilingual readiness"
                      >
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                            {(multilingualSupport.language_modes || []).slice(0, 4).map((item, index) => (
                              <MetaPill key={`lang_mode_${index}`} meta={{ label: item, color: '#38bdf8', tone: 'rgba(56,189,248,0.14)' }} />
                            ))}
                            {!multilingualSupport.language_modes?.length ? (
                              <MetaPill meta={{ label: 'english_or_mixed', color: '#38bdf8', tone: 'rgba(56,189,248,0.14)' }} />
                            ) : null}
                          </div>
                          <div style={{ color: 'var(--text-secondary)', fontSize: '12px', lineHeight: 1.6 }}>
                            {multilingualSupport.note || 'Jolly and Aina can stay grounded even when hearing memory is mixed-language.'}
                          </div>
                          <div style={{ color: 'var(--text-muted)', fontSize: '11px', lineHeight: 1.55 }}>
                            {multilingualSupport.supports_multilingual_dictation ? 'Dictation-backed multilingual hearing memory is available in this matter.' : 'Multilingual support will get stronger once hearing or client-call memory is linked through Jolly.'}
                          </div>
                        </div>
                      </SecondaryInfoCard>
                    </section>
                  </>
                ) : null}

                <section style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '14px' }}>
                  {Object.entries(activeRun.patch_plan || {}).map(([bucketKey, items]) => (
                    <PatchBucketCard
                      key={bucketKey}
                      bucketKey={bucketKey}
                      items={items || []}
                      onStartWithAina={startBucketGuidedPrompt}
                    />
                  ))}
                </section>

                {(activeRun.patch_actions || []).length ? (
                  <section style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
                      <div>
                        <div style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--accent-primary)', marginBottom: '6px' }}>
                          Executable patches
                        </div>
                        <div style={{ color: 'var(--text-secondary)', fontSize: '13px', lineHeight: 1.65 }}>
                          These are now live actions. You can execute them into the matter, send them into research, or ask Aina to walk you through them in the same thread.
                        </div>
                      </div>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '12px' }}>
                      {activeRun.patch_actions.map((action) => (
                        <PatchActionCard
                          key={action.patch_action_id}
                          action={action}
                          executing={executingPatchActionId === action.patch_action_id}
                          onExecute={runPatchAction}
                          onAskAina={askAinaAboutPatchAction}
                          onSendToResearch={sendPatchActionToResearch}
                          onApplyEdit={applyFindingEdit}
                          finding={findingById[action.finding_id]}
                        />
                      ))}
                    </div>
                  </section>
                ) : null}

                <section style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
                  {groupedFindings.map(group => (
                    <div key={group.value} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                        <MetaPill meta={{ label: group.label, color: group.accent, tone: group.tone }} />
                        <span style={{ color: 'var(--text-muted)', fontSize: '12px' }}>
                          {group.findings.length} finding{group.findings.length === 1 ? '' : 's'}
                        </span>
                      </div>
                      {group.findings.map(finding => (
                        <FindingCard
                          key={finding.finding_id}
                          finding={finding}
                          matterId={activeRun?.matter_id || selectedMatterId}
                          destination={destinationByFinding[finding.finding_id] || suggestedDestinationForFinding(finding)}
                          onDestinationChange={(findingId, value) => setDestinationByFinding(prev => ({ ...prev, [findingId]: value }))}
                          onReview={updateFindingReview}
                          onSave={saveFinding}
                          onPreview={previewSource}
                          onSendToResearch={openResearchFromFinding}
                          onApplyEdit={applyFindingEdit}
                          saving={savingFindingId === finding.finding_id}
                          reviewing={reviewingFindingId === finding.finding_id}
                          matterLinked={Boolean(activeRun.matter_id)}
                        />
                      ))}
                    </div>
                  ))}
                </section>

                {/* Aina Interactive Mirror Chat */}
                <section ref={ainaChatSectionRef} style={{ borderRadius: '20px', border: '1px solid var(--border-subtle)', background: 'var(--bg-surface)', overflow: 'hidden' }}>
                  <button
                    type="button"
                    onClick={() => setShowAinaChat(prev => !prev)}
                    style={{
                      width: '100%',
                      padding: '16px 20px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      gap: '12px',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <MessageSquare size={16} style={{ color: 'var(--accent-primary)' }} />
                      <div style={{ textAlign: 'left' }}>
                        <div style={{ fontSize: '14px', fontWeight: 700, color: 'var(--text-primary)', fontFamily: 'var(--font-display)' }}>
                          Chat with Aina
                        </div>
                        <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                          Ask Aina to explain findings, execute fixes, or tell you exactly which files are still missing
                        </div>
                      </div>
                    </div>
                    <ChevronDown size={14} style={{ color: 'var(--text-muted)', transform: showAinaChat ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
                  </button>

                  {showAinaChat && (
                    <div style={{ borderTop: '1px solid var(--border-subtle)', padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                        <div style={{ color: 'var(--text-muted)', fontSize: '12px' }}>
                          {activeAinaThreadId ? 'This conversation is persisted in Aina history.' : 'A new Aina conversation will be created when you send the next message.'}
                        </div>
                        <FeatureHistoryMenu
                          feature="aina"
                          matterId={selectedMatterId || activeRun?.matter_id || ''}
                          workspaceId={matterWorkspace?.workspace_id || initialWorkspace?.workspace_id || ''}
                          label="Recent Aina Chats"
                          dropdownAlign="right"
                        />
                      </div>
                      <div ref={ainaChatListRef} style={{ maxHeight: '320px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '10px', paddingRight: '4px' }}>
                        {ainaChatMessages.length === 0 ? (
                          <p style={{ color: 'var(--text-muted)', fontSize: '12px', padding: '8px 0' }}>
                            Ask Aina to go deeper on any finding, tell you what fix to apply, or list the exact documents and facts still needed.
                          </p>
                        ) : ainaChatMessages.map((msg, idx) => (
                          <div key={idx} style={{
                            padding: '10px 14px',
                            borderRadius: '12px',
                            background: msg.role === 'user' ? 'var(--bg-elevated)' : 'rgba(249,115,22,0.08)',
                            border: `1px solid ${msg.role === 'user' ? 'var(--border-subtle)' : 'rgba(249,115,22,0.2)'}`,
                            fontSize: '13px',
                            color: 'var(--text-primary)',
                            lineHeight: 1.65,
                          }}>
                            <div style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: msg.role === 'user' ? 'var(--text-muted)' : '#f97316', marginBottom: '4px' }}>
                              {msg.role === 'user' ? 'You' : 'Aina'}
                            </div>
                            {msg.role === 'assistant' ? (
                              <div className="markdown-content" style={{ fontSize: '13px', lineHeight: 1.75 }}>
                                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                  {normalizeAinaAssistantContent(msg.content)}
                                </ReactMarkdown>
                              </div>
                            ) : (
                              msg.content
                            )}
                            {msg.isStreaming && <span style={{ display: 'inline-block', width: '6px', height: '12px', background: 'var(--text-muted)', marginLeft: '2px', animation: 'blink 1s step-end infinite' }} />}
                          </div>
                        ))}
                        <div ref={ainaChatEndRef} />
                      </div>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <JollyDictationButton
                          compact
                          label="Jolly"
                          disabled={ainaChatStreaming}
                          onAppendText={appendAinaChatDictation}
                        />
                        <input
                          value={ainaChatInput}
                          onChange={e => setAinaChatInput(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendAinaChat(); } }}
                          placeholder="Ask Aina to explain a finding or challenge your position…"
                          style={{
                            flex: 1,
                            padding: '9px 14px',
                            borderRadius: '12px',
                            border: '1px solid var(--border-default)',
                            background: 'var(--bg-base)',
                            color: 'var(--text-primary)',
                            fontSize: '13px',
                            fontFamily: 'var(--font-ui)',
                          }}
                        />
                        <button
                          type="button"
                          onClick={sendAinaChat}
                          disabled={!ainaChatInput.trim() || ainaChatStreaming}
                          className={`${PRIMARY_BUTTON_CLASS} inline-flex items-center justify-center`}
                          style={{
                            borderRadius: '12px',
                            fontSize: '12px',
                            fontWeight: 600,
                          }}
                        >
                          {ainaChatStreaming ? <Loader2 size={13} className="animate-spin" /> : <Search size={13} />}
                        </button>
                      </div>
                    </div>
                  )}
                </section>
              </>
            ) : showMinimalLanding ? null : (
              <div className="advoq-card" style={{ padding: '38px 20px', borderRadius: '22px', color: 'var(--text-secondary)', lineHeight: 1.8 }}>
                Run Aina to get citation-backed vulnerabilities, adversarial framing, and a patch plan that can flow back into Sabha.
              </div>
            )}
          </div>

          {!embedded && !showMinimalLanding ? (
            <aside style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
              <SecondaryInfoCard
                eyebrow="Matter link"
                title="Where Aina writes back"
                action={selectedMatterId ? (
                  <button type="button" className="app-chat-button" onClick={() => navigate(`/war-room/${selectedMatterId}`)}>
                    <ArrowRight size={13} /> Open Sabha
                  </button>
                ) : null}
              >
                <div style={{ color: 'var(--text-secondary)', fontSize: '13px', lineHeight: 1.75 }}>
                  Saved findings can flow into open questions, risk register, hearing pack, research queue, client follow-up, team tasks, and matter notes. Matter chat then retrieves them with provenance.
                </div>
              </SecondaryInfoCard>

              <SecondaryInfoCard
                eyebrow="Run history"
                title="Recent Aina passes"
                action={loadingRuns ? <Loader2 size={15} className="animate-spin" style={{ color: 'var(--text-muted)' }} /> : null}
              >
                {historyContent}
              </SecondaryInfoCard>

              <SecondaryInfoCard
                eyebrow="Patch workflow"
                title="Quick hand-off paths"
              >
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {quickPaths.map((item, index) => (
                    <div
                      key={`path_${index}`}
                      style={{
                        padding: '12px 13px',
                        borderRadius: '14px',
                        border: '1px solid var(--border-subtle)',
                        background: 'var(--bg-elevated)',
                        color: 'var(--text-secondary)',
                        fontSize: '13px',
                        lineHeight: 1.65,
                      }}
                    >
                      {item}
                    </div>
                  ))}
                </div>
              </SecondaryInfoCard>
            </aside>
          ) : null}
        </section>
      </div>

      {!onPreview ? (
        <LinkPreviewPanel
          url={internalPreviewUrl}
          title={internalPreviewTitle}
          onClose={() => {
            setInternalPreviewUrl('');
            setInternalPreviewTitle('');
          }}
        />
      ) : null}
    </>
  );
}
